import { normalizeEdgeFinish } from "../domain/edgeFinish";
import { create } from "zustand";
import { cornerArms, cornerBlindFor, cornerDoorWidthMm, cornerFillerMm, cornerNeighbourFillerMm, cornerMezzanineMm, cornerSide, CORNER_BLIND_MAX_MM, CORNER_BLIND_MIN_MM, CORNER_MEZZANINE_MAX_MM, CORNER_MEZZANINE_MIN_MM } from "../domain/corner";
import { cornerCanAddRail, cornerItemPosition, cornerRailPlacement, fitCornerItems } from "../domain/cornerInternals";
import {
  appendCatalogShelf,
  replaceWithCatalogComposition,
  replaceWithCatalogShelf
} from "../domain/catalogImport";
import { persist } from "zustand/middleware";
import { handleForFacade, INTERNALS, normalizeHandleChoice } from "../catalog";
import { normalizeSideMirrors } from "../domain/sideMirrors";
import { normalizeBackDecor } from "../domain/backPanel";
import { normalizeDoorFacades } from "../domain/doorFacades";
import { normalizeDrawerDecor, normalizeShelfDecor } from "../domain/interiorDecor";
import { isChestGlassDoor, normalizeChestDrawerPaint, normalizeTopDecor, withChestAutoFronts } from "../domain/chest";
import { CARCASS_MM } from "../domain/carcass";
import { clampDividerX, defaultDividerX, internalItemPieces, internalItemSpanMm, splitItemByCompartments } from "../domain/compartments";
import { normalizeLightPosition, normalizeLightTemperature, normalizeShelvingLighting } from "../domain/lighting";
import {
  internalItemMinimumY,
  resizeDividerFromBottom,
  resolveDividerEdge,
  resolveDividerMove,
  fitDividerToSupports,
  dividerSpanLimits,
  resolveInternalItemY,
  resizeInternalDrawer,
  snapDividerHeightToShelves
} from "../domain/internalLayout";
import {
  cabinetBaseHeightMm,
  cabinetBaseSetbackMm,
  cabinetBaseType,
  DEFAULT_BASE_HEIGHT_MM,
  DEFAULT_BASE_SETBACK_MM,
  installationSpanMm,
  leftFillerMm,
  rightFillerMm,
  topFillerMm
} from "../domain/installation";
import {
  buildFillingPreset,
  copyFillingLayout,
  fillingPresetFits
} from "../domain/fillingPresets";
import { isShelvingDesign } from "../domain/shelvingDesigns";
import { normalizeShelvingTv } from "../domain/shelvingTv";
import {
  applyShelvingBulkPlan,
  shelvingBulkPlan,
  shelvingMergeKeptCell
} from "../domain/shelvingBulk";
import {
  applyRoomSize,
  materializeWallOffsets,
  MAX_ROOM_WIDTH_MM,
  moduleOverlapsFloor,
  placeModuleGroup,
  resolveModuleGroupWallOffset,
  roomWidthMinimumMm,
  roomWidthMm,
  separateOverlappingFloorModules,
  withRoomWidth,
  cornerNeighbour
} from "../domain/layout";
import {
  baseModuleFor,
  cabinetHeightSplit,
  cabinetSupportsToCeiling,
  cabinetToCeilingSplit,
  shelvingHeightSplit,
  shelvingMezzanineHeightRange,
  SHELVING_MAX_MEZZANINE_HEIGHT_MM,
  cabinetRunModules,
  ceilingGapMm,
  ceilingHeightMm,
  DEFAULT_CEILING_GAP_MM,
  doorLeafCount,
  MAX_MEZZANINE_HEIGHT_MM,
  MAX_MODULE_HEIGHT_MM,
  maxModuleWidth,
  mezzanineFor,
  mezzanineHeightSplit,
  maxCabinetRunWidthOnBackWall,
  cornerAddBlocker,
  shelvingUpperFor,
  usableRoomHeightMm,
  MIN_MODULE_HEIGHT_MM
} from "../domain/moduleRules";
import { cabinetLightMode, cabinetSupportsLighting } from "../domain/cabinetLighting";
import {
  cabinetDrawerFrontHeightMm,
  cabinetDrawerLayout,
  CABINET_DRAWER_MAX_COUNT,
  handleDoorHeightMm,
  supportsCabinetDrawers
} from "../domain/cabinetDrawers";
import {
  handleCenterYmm,
  handleEdgeOffsetMm,
  handleLengthMm,
  handleOrientation,
  integratedHandleStyle
} from "../domain/handles";
import {
  normalizeFacadeColorCode,
  normalizeFacadeColorHex
} from "../domain/facades";
import {
  normalizeShelvingColorCode,
  normalizeShelvingColorHex
} from "../domain/shelvingFinishes";
import { isLiftType } from "../domain/flapFronts";
import { clamp, createId, initialProject, snap } from "../domain/project";
import {
  autoProjectName,
  createProjectNumber,
  isAutoProjectName,
  PROJECT_NUMBER_PATTERN,
  projectProductFamilies
} from "../domain/productWorkflow";
import { applyDiagonalArea, type DiagonalArea } from "../domain/diagonalSections";
import {
  inferRoomStyle,
  isRoomStyle,
  roomStylePatch
} from "../domain/roomPresets";
import {
  defaultShelvingCell,
  getShelvingDiagonalSections,
  getShelvingCellSpanRect,
  maxShelvingColumnsForWidth,
  maxShelvingRowsForHeight,
  normalizeShelvingRatios,
  normalizeShelvingDiagonalCells,
  normalizeShelvingMergedCells,
  shelvingMergeProblem,
  getShelvingGridLayout,
  shelvingMainDividerMaxAngle,
  shelvingUprightRotatableRows,
  parseShelvingCellId,
  shelvingCellId,
  SHELVING_MAX_DEPTH_MM,
  SHELVING_MAX_HEIGHT_MM,
  SHELVING_MAX_WIDTH_MM,
  SHELVING_MIN_COLUMNS,
  SHELVING_MIN_DEPTH_MM,
  SHELVING_MIN_HEIGHT_MM,
  SHELVING_MIN_ROWS,
  SHELVING_MIN_WIDTH_MM,
  SHELVING_DRAWER_MAX_COUNT,
  SHELVING_STANDARD_ANGLE_DEG,
  standardShelvingAngle,
  gridCellWidth,
  moveOneShelvingUpright,
  shelvingKindAllowsRowColumns,
  SHELVING_MIN_CELL_WIDTH_MM,
  chestMaxColumns,
  normalizeShelvingFacadeOverhang
} from "../domain/shelving";
import { decorPriceMaterial, resolveDecor } from "../domain/decors";
import {
  alignEndModuleOffsets,
  boundaryCoreModule,
  canAttachEndModule,
  END_MODULE_MAX_DEPTH_MM,
  END_MODULE_MAX_TIERS,
  END_MODULE_MAX_WIDTH_MM,
  END_MODULE_MIN_DEPTH_MM,
  END_MODULE_MIN_TIERS,
  END_MODULE_MIN_WIDTH_MM,
  endModuleForTargetSide,
  endModuleAttachmentOffset,
  endModuleAddTarget,
  endModuleShape,
  endModuleSide,
  endModuleTarget,
  endModuleTierCount,
  isEndModuleAttachmentTarget,
  syncAttachedEndModuleMaterials
} from "../domain/endModules";
import type {
  DecorDensity,
  FacadePatternType,
  FacadeType,
  FillingPresetType,
  InternalItem,
  ItemType,
  ModuleType,
  PlannerProject,
  RoomSettings,
  RoomStylePreset,
  ShelvingCellConfig,
  ShelvingDividerMode,
  WardrobeModule
} from "../types";

interface PlannerState {
  addCatalogShelf: (module: WardrobeModule) => void;
  replaceWithCatalogShelf: (module: WardrobeModule) => void;
  /** Готовый шкаф из витрины: состав корпусов заменяет текущее изделие. */
  replaceWithCatalogComposition: (modules: WardrobeModule[]) => void;
  loadProject: (project: PlannerProject) => void;
  project: PlannerProject;
  past: PlannerProject[];
  future: PlannerProject[];
  selectModule: (id: string) => void;
  clearSelection: () => void;
  selectItem: (moduleId: string, itemId: string) => void;
  selectShelvingCell: (moduleId: string, cellId: string) => void;
  addModule: (type: ModuleType, options?: Partial<WardrobeModule>) => void;
  moveModule: (id: string, wallOffset: number) => void;
  removeSelectedModule: () => void;
  updateModule: (id: string, patch: Partial<WardrobeModule>) => void;
  /**
   * Фасад выбранного шкафа — на все шкафы проекта (видео Сергея 24.10): двери,
   * отделка, цвет, фрезеровка, ручка. Антресоли следуют за своими шкафами.
   * Возвращает, сколько шкафов изменилось.
   */
  applyFacadeToAllCabinets: (id: string) => number;
  /** Корпус выбранного шкафа (декор, кромка, цоколь или ножки) — на все шкафы проекта (29.10). */
  applyCarcassToAllCabinets: (id: string) => number;
  setCabinetTotalHeight: (id: string, height: number) => void;
  /**
   * Высота антресоли при неизменной общей высоте: антресоль выше — корпус
   * ниже. Для всего ряда корпусов с антресолями.
   */
  setMezzanineHeight: (id: string, height: number) => void;
  /** «Антресоль до потолка» у пенала (№ 11); false — если потолок слишком низкий. */
  setCabinetToCeiling: (id: string, enabled: boolean) => void;
  setCabinetTotalWidth: (id: string, width: number) => void;
  setShelvingTotalHeight: (id: string, height: number) => void;
  /**
   * Высота антресоли стеллажа при той же общей высоте (видео Сергея, 28.10):
   * антресоль выше — нижний стеллаж ниже. Можно вызвать у стеллажа или у антресоли.
   */
  setShelvingMezzanineHeight: (id: string, height: number) => void;
  addItem: (type: ItemType) => void;
  applyFillingPreset: (preset: FillingPresetType) => void;
  copyFillingToModule: (targetModuleId: string) => void;
  setDecorDensity: (density: DecorDensity) => void;
  updateItem: (moduleId: string, itemId: string, patch: Partial<InternalItem>) => void;
  setDividerEdge: (moduleId: string, itemId: string, edge: "bottom" | "top", value: number) => void;
  setShelfPieceLight: (moduleId: string, itemId: string, pieceCenterX: number, on: boolean) => void;
  /**
   * Сдвинуть деталь только в одном отсеке шкафа: строка на всю ширину
   * сначала делится на отдельные детали по отсекам, остальные остаются на месте.
   */
  moveItemInCompartment: (moduleId: string, itemId: string, x: number, y: number) => void;
  updateShelvingCell: (
    moduleId: string,
    cellId: string,
    patch: Partial<ShelvingCellConfig>
  ) => void;
  /** Одна правка для всех ячеек — фасады и ручки комода общие. Одним шагом «Отменить». */
  updateAllShelvingCells: (moduleId: string, patch: Partial<ShelvingCellConfig>) => void;
  setDiagonalSection: (moduleId: string, area: DiagonalArea, sourceId?: string, replaceExisting?: boolean) => boolean;
  /** Объединить прямоугольник ячеек в одну. Возвращает причину отказа или null. */
  /**
   * Объединить прямоугольник ячеек в одну. keepCellId — чьи настройки
   * (фасад, полки, подсветка) получает объединённая ячейка; по умолчанию
   * левой нижней. Возвращает причину отказа или null.
   */
  mergeShelvingCells: (moduleId: string, area: { left: number; right: number; bottom: number; top: number }, keepCellId?: string) => string | null;
  /** Разъединить объединённую ячейку обратно на исходные. */
  splitShelvingCell: (moduleId: string, cellId: string) => void;
  /**
   * Повернуть стойку сетки: на всю высоту (все ряды, где по обе стороны
   * открытые ячейки) или только участок в одном ряду. 0 — выпрямить.
   * Возвращает ряды, где стойка повёрнута.
   */
  setShelvingUprightAngle: (moduleId: string, dividerIndex: number, angle: number, onlyRow?: number) => number[];
  /**
   * Ширина ячейки в свету (№ 19): «all» — во всех рядах с общей раскладкой,
   * «row» — только в этом ряду (у ряда появляется своя раскладка). Меняется
   * соседняя ячейка справа (у последней — слева), остальные стоят на месте.
   */
  setShelvingCellWidth: (moduleId: string, row: number, column: number, width: number, scope: "all" | "row") => boolean;
  /** Ряд снова по общей раскладке колонок. */
  resetShelvingRowColumns: (moduleId: string, row: number) => void;
  removeDiagonalSection: (moduleId: string, cellId: string) => void;
  /** Stamps one cell's filling onto other cells of the same shelving unit. */
  applyShelvingCellFilling: (
    moduleId: string,
    sourceCellId: string,
    targetCellIds: readonly string[]
  ) => number;
  removeSelectedItem: () => void;
  setMaterial: (material: PlannerProject["carcassMaterial"]) => void;
  /** Артикул декора Egger для выбранного изделия, иначе для всего проекта. */
  setDecor: (code: string) => void;
  setProjectName: (name: string) => void;
  setRoomStyle: (style: RoomStylePreset) => void;
  setRoom: (patch: Partial<RoomSettings>) => void;
  setRoomWidth: (width: number) => void;
  undo: () => void;
  redo: () => void;
  /** Новый проект. keepRoom — оставить комнату (потолок, стены, пол, свет) прежней. */
  reset: (options?: { keepRoom?: boolean }) => void;
}


/** Позиции дополнительных полок и перегородок ячейки: числа 0..1, не больше четырёх. */
const normalizeBoardPositions = (values: unknown) => {
  if (!Array.isArray(values)) return undefined;
  const positions = values
    .filter((value): value is number => typeof value === "number" && Number.isFinite(value))
    .map((value) => clamp(value, 0, 1))
    .slice(0, 4);
  return positions.length ? positions : undefined;
};

/** Флаги стеклянных полок — по одному на полку из moreShelves; ни одной стеклянной — поля нет. */
const normalizeGlassFlags = (flags: unknown, positions: number[] | undefined) => {
  if (!positions?.length || !Array.isArray(flags)) return undefined;
  const normalized = positions.map((_, index) => flags[index] === true);
  return normalized.some(Boolean) ? normalized : undefined;
};

const clone = (project: PlannerProject): PlannerProject => structuredClone(project);

/**
 * Ячейки комода: по одной на колонку, без объединений. Колонка — ящики или
 * фасад: дверца, две дверцы, откидной (Сергей, 15.10); новая колонка — ящики.
 */
const chestCells = (module: WardrobeModule, columns: number): ShelvingCellConfig[] => {
  const cells = module.shelvingCells ?? [];
  const template = cells.find((cell) => cell.content === "drawers") ?? cells[0];
  return Array.from({ length: columns }, (_, column) => {
    const own = cells.find((cell) => cell.column === column && cell.row === 0);
    const source = own ?? template;
    const door = own?.content === "door";
    return {
      ...defaultShelvingCell(module.id, column, 0),
      ...(source ?? {}),
      id: shelvingCellId(module.id, column, 0),
      column,
      row: 0,
      content: door ? "door" as const : "drawers" as const,
      drawerCount: source?.drawerCount ?? 3,
      // Стекло — только на дверце; ящик и зеркало в комоде — плитные.
      ...(source?.facade === "mirror" || (source?.facade === "glass" && !door) ? { facade: "wood" as const } : {}),
      // Новая колонка из образца-дверцы — ящики, без настроек дверцы.
      ...(door ? {} : { doorOpening: undefined, doorLift: undefined, chestFrontManual: undefined }),
      // Откидной над ящиками (16.10) — только у своей колонки с ящиками, новые колонки его не копируют.
      ...(door || !own ? { chestTopFlap: undefined, chestTopFlapGlass: undefined } : {
        chestTopFlap: Number.isFinite(own.chestTopFlap) && Number(own.chestTopFlap) > 0 ? Math.round(Number(own.chestTopFlap)) : undefined,
        chestTopFlapGlass: own.chestTopFlap && own.chestTopFlapGlass ? true as const : undefined }),
      backPanel: false,
      // За дверцей комода — одна полка; перегородок, поворотов и подсветки нет.
      extraShelf: door ? Boolean(own?.extraShelf) : false,
      extraShelfPosition: door ? own?.extraShelfPosition : undefined,
      extraDivider: false,
      extraDividerPosition: undefined,
      extraDividerMode: undefined,
      extraDividerAngle: undefined,
      moreShelves: undefined,
      moreShelvesGlass: undefined,
      extraShelfGlass: undefined,
      moreDividers: undefined,
      leftDividerAngle: undefined,
      mergeColumnSpan: undefined,
      mergeRowSpan: undefined,
      diagonalColumnSpan: undefined,
      diagonalRowSpan: undefined,
      lighting: undefined
    };
  });
};

/**
 * Свои ширины ячеек ряда (№ 19): только при той же сетке колонок; ряд, совпавший
 * с общей раскладкой, своей не хранит. Ряды внутри объединённой по вертикали
 * ячейки или диагональной секции — всегда по общей раскладке.
 */
const normalizeRowColumnRatios = (
  source: Array<number[] | null> | undefined,
  common: number[],
  rows: number,
  lockedRows: Set<number>
): Array<number[] | null> | undefined => {
  if (!Array.isArray(source)) return undefined;
  const result = Array.from({ length: rows }, (_, row) => {
    const own = source[row];
    if (lockedRows.has(row) || !Array.isArray(own) || own.length !== common.length) return null;
    if (!own.every((value) => Number.isFinite(value) && value > 0)) return null;
    const ratios = normalizeShelvingRatios(own, common.length);
    return ratios.every((ratio, index) => Math.abs(ratio - common[index]!) < 1e-4) ? null : ratios;
  });
  return result.some(Boolean) ? result : undefined;
};

/** Ряды, которые закрывает объединённая по вертикали ячейка или диагональная секция. */
const rowsInTallSpans = (cells: ShelvingCellConfig[]) => {
  const locked = new Set<number>();
  cells.forEach((cell) => {
    const span = cell.content === "diagonal" ? cell.diagonalRowSpan ?? 1 : cell.mergeRowSpan ?? 1;
    if (span > 1) for (let row = cell.row; row < cell.row + span; row++) locked.add(row);
  });
  return locked;
};

const createStackedMezzanine = (
  base: WardrobeModule,
  height: number
): WardrobeModule => ({
  ...structuredClone(base),
  id: createId("module"),
  type: "mezzanine",
  stackedOnId: base.id,
  wallOffset: base.wallOffset,
  width: base.width,
  height,
  depth: base.depth,
  baseType: "floor",
  baseHeight: 0,
  fillerLeft: base.fillerLeft,
  fillerRight: base.fillerRight,
  fillerTop: base.fillerTop,
  items: []
});

const createStackedShelving = (
  base: WardrobeModule,
  height: number
): WardrobeModule => {
  const rows = clamp(
    Math.ceil(height / 400),
    SHELVING_MIN_ROWS,
    maxShelvingRowsForHeight(height, "floor")
  );
  return {
    ...structuredClone(base),
    id: createId("module"),
    type: "shelving",
    stackedOnId: base.id,
    wallOffset: base.wallOffset,
    width: base.width,
    height,
    depth: base.depth,
    door: "open",
    items: [],
    shelvingRows: rows,
    shelvingRowRatios: undefined,
    // Стойки антресоли — по общей раскладке стеллажа: свои ширины его рядов
    // относились к его рядам и сдвигали стойки антресоли (видео Сергея, 28.10).
    shelvingRowColumnRatios: undefined,
    shelvingBase: "floor",
    shelvingCells: [],
    shelvingDesign: undefined,
    baseType: undefined,
    baseHeight: undefined,
    baseSetback: undefined,
    fillerLeft: 0,
    fillerRight: 0,
    fillerTop: 0
  };
};

/**
 * Fields a ready-made shelving scheme defines. Touching any of them by hand
 * means the unit no longer matches the scheme it was started from, so the
 * badge has to disappear. Finish and base stay out on purpose: applying a
 * scheme preserves them, so changing them is not a divergence.
 */
const SHELVING_DESIGN_FIELDS = [
  "width",
  "height",
  "depth",
  "shelvingColumns",
  "shelvingRows",
  "shelvingColumnRatios",
  "shelvingRowColumnRatios",
  "shelvingRowRatios",
  "shelvingBackPanel",
  "shelvingCells"
] as const;

const patchDivergesFromShelvingDesign = (patch: Partial<WardrobeModule>) =>
  SHELVING_DESIGN_FIELDS.some((field) =>
    Object.prototype.hasOwnProperty.call(patch, field)
  );

const clearShelvingDesignLink = (module: WardrobeModule) => {
  if (module.type === "shelving") module.shelvingDesign = undefined;
};

const normalizeFacade = (value: unknown): FacadeType =>
  (["wood", "glass", "satin", "mirror", "gloss"] as string[]).includes(
    value as string
  )
    ? (value as FacadeType)
    : "wood";

const normalizeFacadePattern = (
  value: unknown,
  legacyGloss = false
): FacadePatternType =>
  ([
    "plain",
    "art001",
    "art005",
    "art006",
    "art007",
    "art008",
    "art009",
    "art010",
    "art012",
    "art023",
    "art036",
    "art037"
  ] as string[]).includes(value as string)
    ? (value as FacadePatternType)
    : legacyGloss
      ? "art009"
      : "plain";

/**
 * Декор — ведущее поле, материал вычисляется из него: прайс и PDF по-прежнему
 * считают по материалу, а проекты, сохранённые до каталога, получают декор,
 * подобранный к их прежнему материалу.
 */
const normalizeDecor = (module: WardrobeModule): WardrobeModule => {
  const carcass = resolveDecor(module.decor, module.material);
  module.decor = carcass.code;
  module.material = decorPriceMaterial(carcass.code);
  if (module.facadeDecor !== undefined || module.facadeMaterial !== undefined) {
    const facade = resolveDecor(module.facadeDecor, module.facadeMaterial);
    module.facadeDecor = facade.code;
    module.facadeMaterial = decorPriceMaterial(facade.code);
  }
  (module.shelvingCells ?? []).forEach(cell => {
    if (cell.facadeDecor === undefined && cell.facadeMaterial === undefined) return;
    const facade = resolveDecor(cell.facadeDecor, cell.facadeMaterial);
    cell.facadeDecor = facade.code;
    cell.facadeMaterial = decorPriceMaterial(facade.code);
  });
  return module;
};

const normalizeModule = (module: WardrobeModule): WardrobeModule => {
  if (module.type === "shelving") {
    const width = clamp(
      Math.round(module.width),
      SHELVING_MIN_WIDTH_MM,
      SHELVING_MAX_WIDTH_MM
    );
    const height = clamp(
      Math.round(module.height),
      SHELVING_MIN_HEIGHT_MM,
      SHELVING_MAX_HEIGHT_MM
    );
    const depth = clamp(
      Math.round(module.depth),
      SHELVING_MIN_DEPTH_MM,
      SHELVING_MAX_DEPTH_MM
    );
    const base = module.shelvingBase ?? "plinth";
    // Комод: один ряд, каждая колонка — ячейка с ящиками. Новые колонки
    // берут отделку и число ящиков у уже настроенной.
    const chest = module.shelvingKind === "chest" && !module.stackedOnId;
    const minimumColumns = chest ? 1 : SHELVING_MIN_COLUMNS;
    const columns = clamp(
      Math.round(module.shelvingColumns ?? (chest ? 2 : 4)),
      minimumColumns,
      // У комода колонки не уже ящика: при сужении лишние колонки убираются.
      chest ? chestMaxColumns(width) : maxShelvingColumnsForWidth(width, minimumColumns)
    );
    const rows = chest ? 1 : clamp(
      Math.round(module.shelvingRows ?? 5),
      SHELVING_MIN_ROWS,
      maxShelvingRowsForHeight(height, base)
    );
    const sourceCells = chest ? chestCells(module, columns) : (module.shelvingCells ?? []);
    const seenCells = new Set<string>();
    const normalizedCells = sourceCells
      .filter(
        (cell) =>
          Number.isInteger(cell.column) &&
          Number.isInteger(cell.row) &&
          cell.column >= 0 &&
          cell.column < columns &&
          cell.row >= 0 &&
          cell.row < rows
      )
      .filter((cell) => {
        const id = shelvingCellId(module.id, cell.column, cell.row);
        if (seenCells.has(id)) return false;
        seenCells.add(id);
        return true;
      })
      .map((cell) => {
        const sourceFacade = normalizeFacade(cell.facade);
        const legacyGloss = sourceFacade === "gloss";
        const handle = normalizeHandleChoice(cell.handle);
        const facade = legacyGloss ? "satin" : sourceFacade;
        const content = (
          ["open", "door", "drawers", "diagonal"] as string[]
        ).includes(cell.content as string)
          ? cell.content
          : "open";
        const inferredDividerMode: ShelvingDividerMode =
          Math.abs((Number(cell.extraDividerPosition) || 0.5) - 0.5) > 0.001
            ? "offset"
            : "centered";
        const requestedDividerMode = (
          ["centered", "offset", "angled"] as string[]
        ).includes(cell.extraDividerMode as string)
          ? cell.extraDividerMode as ShelvingDividerMode
          : inferredDividerMode;
        const dividerMode = content === "open" || requestedDividerMode !== "angled"
          ? requestedDividerMode
          : "centered";
        const requestedDividerAngle = Number.isFinite(cell.extraDividerAngle)
          ? Number(cell.extraDividerAngle)
          : 10;
        return {
          ...defaultShelvingCell(module.id, cell.column, cell.row),
          ...cell,
          id: shelvingCellId(module.id, cell.column, cell.row),
          facade,
          facadePattern: normalizeFacadePattern(
            cell.facadePattern,
            legacyGloss
          ),
          facadeColorHex: normalizeFacadeColorHex(cell.facadeColorHex),
          facadeColorCode: normalizeFacadeColorCode(cell.facadeColorCode),
          facadePaintSides: cell.facadePaintSides === "two" && facade === "satin" ? "two" as const : undefined,
          // Две дверцы и откидные фасады (09.10) — только у фасада ячейки.
          doorOpening: content === "door" && (cell.doorOpening === "double" || cell.doorOpening === "up" || cell.doorOpening === "down")
            ? cell.doorOpening : undefined,
          doorLift: isLiftType(cell.doorLift) ? cell.doorLift : undefined,
          extraShelf: content === "drawers" || content === "diagonal" ||
            (Boolean(cell.extraDivider) && dividerMode === "angled")
            ? false
            : Boolean(cell.extraShelf),
          extraShelfPosition: clamp(
            Number.isFinite(cell.extraShelfPosition)
              ? Number(cell.extraShelfPosition)
              : 0.5,
            0,
            1
          ),
          extraDivider: content === "drawers" || content === "diagonal"
            ? false : Boolean(cell.extraDivider),
          moreShelves: content === "drawers" || content === "diagonal" ||
            (Boolean(cell.extraDivider) && dividerMode === "angled")
            ? undefined
            : normalizeBoardPositions(cell.moreShelves),
          // Стеклянные полки: флаг первой полки и флаги остальных в их порядке.
          extraShelfGlass: content !== "drawers" && content !== "diagonal" &&
            !(Boolean(cell.extraDivider) && dividerMode === "angled") &&
            cell.extraShelf && cell.extraShelfGlass ? true : undefined,
          moreShelvesGlass: normalizeGlassFlags(cell.moreShelvesGlass,
            content === "drawers" || content === "diagonal" || (Boolean(cell.extraDivider) && dividerMode === "angled")
              ? undefined : normalizeBoardPositions(cell.moreShelves)),
          moreDividers: content === "drawers" || content === "diagonal"
            ? undefined
            : normalizeBoardPositions(cell.moreDividers),
          lighting: content === "drawers" || content === "diagonal"
            ? undefined
            : normalizeShelvingLighting(cell.lighting) === "none" ? undefined : normalizeShelvingLighting(cell.lighting),
          lightTemperature: normalizeLightTemperature(cell.lightTemperature),
          lightPosition: normalizeLightPosition(cell.lightPosition),
          extraDividerPosition: clamp(
            Number.isFinite(cell.extraDividerPosition) ? Number(cell.extraDividerPosition) : 0.5,
            0, 1
          ),
          extraDividerMode: dividerMode,
          // Один стандартный угол (№ 23): старые 5°, 15° и т. п. становятся 10° в ту же сторону.
          extraDividerAngle: dividerMode === "angled"
            ? standardShelvingAngle(requestedDividerAngle) || SHELVING_STANDARD_ANGLE_DEG
            : 0,
          leftDividerAngle: standardShelvingAngle(cell.leftDividerAngle),
          content,
          diagonalColumnSpan: clamp(
            Math.round(cell.diagonalColumnSpan ?? 2),
            1,
            columns - cell.column
          ),
          diagonalRowSpan: clamp(
            Math.round(cell.diagonalRowSpan ?? 2),
            1,
            rows - cell.row
          ),
          handle: handleForFacade(handle, facade),
          // Интегрированная ручка ящиков: участок по центру или на всю длину (24.10).
          integratedHandleStyle: handleForFacade(handle, facade) === "integrated" && cell.integratedHandleStyle === "topPart"
            ? "topPart" as const : undefined,
          drawerCount: clamp(Math.round(cell.drawerCount ?? 2), 1, SHELVING_DRAWER_MAX_COUNT)
        };
      });
    const normalizedContent = new Map(
      normalizedCells.map(cell => [cell.id, cell.content])
    );
    normalizedCells.forEach(cell => {
      if (cell.column <= 0) {
        cell.leftDividerAngle = 0;
        return;
      }
      if (!cell.leftDividerAngle) return;
      const leftContent = normalizedContent.get(
        shelvingCellId(module.id, cell.column - 1, cell.row)
      ) ?? "open";
      if (cell.content !== "open" || leftContent !== "open") {
        cell.leftDividerAngle = 0;
      }
    });
    const shelvingCells = normalizeShelvingMergedCells(
      module.id,
      normalizeShelvingDiagonalCells(
        module.id,
        normalizedCells,
        columns,
        rows
      ),
      columns,
      rows
    );
    const normalizedShelving: WardrobeModule = {
      ...module,
      width,
      height,
      depth,
      door: "open",
      items: [],
      decorDensity: "normal",
      fillingPreset: undefined,
      shelvingColumns: columns,
      shelvingRows: rows,
      shelvingColumnRatios: normalizeShelvingRatios(
        module.shelvingColumnRatios,
        columns
      ),
      shelvingRowRatios: normalizeShelvingRatios(
        module.shelvingRowRatios,
        rows
      ),
      shelvingRowColumnRatios: chest ? undefined : normalizeRowColumnRatios(
        module.shelvingRowColumnRatios,
        normalizeShelvingRatios(module.shelvingColumnRatios, columns),
        rows,
        rowsInTallSpans(shelvingCells)
      ),
      shelvingBase: base,
      shelvingKind: chest ? "chest" : undefined,
      shelvingTv: normalizeShelvingTv(module.shelvingTv),
      shelvingBackPanel: Boolean(module.shelvingBackPanel),
      // Накладные — по умолчанию, поэтому хранится только «вкладные».
      shelvingFacadeMount: module.shelvingFacadeMount === "inset" ? "inset" : undefined,
      // «Свет на пол» и «свет на стену» Сергей убрал (видео 24.10): такое не делают.
      // В старых проектах и моделях они сбрасываются.
      shelvingPlinthLight: undefined,
      shelvingWallLight: undefined,
      shelvingUprightStyle: "straight",
      shelvingShelfStyle: "straight",
      shelvingColorHex: normalizeShelvingColorHex(module.shelvingColorHex),
      shelvingColorCode: normalizeShelvingColorCode(module.shelvingColorCode),
      // Корпус и столешница в эмали (28.10): код без цвета не хранится.
      carcassEnamelHex: normalizeShelvingColorHex(module.carcassEnamelHex),
      carcassEnamelCode: normalizeShelvingColorHex(module.carcassEnamelHex) ? normalizeShelvingColorCode(module.carcassEnamelCode) : undefined,
      topEnamelHex: chest ? normalizeShelvingColorHex(module.topEnamelHex) : undefined,
      topEnamelCode: chest && normalizeShelvingColorHex(module.topEnamelHex) ? normalizeShelvingColorCode(module.topEnamelCode) : undefined,
      shelvingDesign: isShelvingDesign(module.shelvingDesign)
        ? module.shelvingDesign
        : undefined,
      baseType: undefined,
      baseHeight: undefined,
      baseSetback: undefined,
      fillerLeft: 0,
      fillerRight: 0,
      fillerTop: 0,
      backDecor: normalizeBackDecor(module),
      edgeFinish: normalizeEdgeFinish(module.edgeFinish),
      // Комод (15.10): своя столешница, свес фасадов, покраска ящиков внутри.
      topDecor: chest ? normalizeTopDecor({ ...module, type: "shelving", shelvingKind: "chest" }) : undefined,
      shelvingFacadeOverhang: chest ? normalizeShelvingFacadeOverhang(module.shelvingFacadeOverhang) : undefined,
      ...normalizeChestDrawerPaint(module, chest),
      shelvingCells
    };
    // Комод: открывание фасадов колонок по их размеру, если не выбрано вручную (16.10).
    return chest ? withChestAutoFronts(normalizedShelving) : normalizedShelving;
  }
  if (module.type === "end") {
    const height = clamp(
      Math.round(module.height),
      MIN_MODULE_HEIGHT_MM,
      MAX_MODULE_HEIGHT_MM
    );
    const normalized: WardrobeModule = {
      ...module,
      width: clamp(
        Math.round(module.width),
        END_MODULE_MIN_WIDTH_MM,
        END_MODULE_MAX_WIDTH_MM
      ),
      height,
      depth: clamp(
        Math.round(module.depth),
        END_MODULE_MIN_DEPTH_MM,
        END_MODULE_MAX_DEPTH_MM
      ),
      door: "open",
      facade: "wood",
      facadePattern: "plain",
      handle: "push",
      handleLength: undefined,
      handleCenterY: undefined,
      handleEdgeOffset: undefined,
      handleOrientation: undefined,
      items: [],
      fillingPreset: undefined,
      decorDensity: "minimal",
      baseType: cabinetBaseType(module),
      baseHeight: cabinetBaseHeightMm(module),
      baseSetback: cabinetBaseSetbackMm(module),
      fillerLeft: 0,
      fillerRight: 0,
      fillerTop: 0,
      endSide: endModuleSide(module),
      endShape: endModuleShape(module),
      endShelfCount: endModuleTierCount(module)
    };
    return normalized;
  }
  const height = clamp(
    Math.round(module.height),
    MIN_MODULE_HEIGHT_MM,
    module.type === "mezzanine"
      ? MAX_MEZZANINE_HEIGHT_MM
      : MAX_MODULE_HEIGHT_MM
  );
  const depth = clamp(Math.round(module.depth), 300, 700);
  const handle = normalizeHandleChoice(module.handle);
  const door = module.door === "open" ? "open" : "hinged";
  const sourceFacade = normalizeFacade(module.facade);
  const legacyGloss = sourceFacade === "gloss";
  const facade = legacyGloss ? "satin" : sourceFacade;
  const facadePattern = normalizeFacadePattern(
    module.facadePattern,
    legacyGloss
  );
  const facadeMaterial = (
    ["warmOak", "linen", "matteWhite", "walnut", "graphite"] as string[]
  ).includes(module.facadeMaterial as string)
    ? module.facadeMaterial
    : module.material ?? "matteWhite";
  const facadeColor = (
    ["white", "cashmere", "sage", "graphite"] as string[]
  ).includes(module.facadeColor as string)
    ? module.facadeColor
    : "white";
  const facadeColorHex = normalizeFacadeColorHex(module.facadeColorHex);
  const facadeColorCode = normalizeFacadeColorCode(module.facadeColorCode);
  const normalized = {
    ...module,
    door,
    doorHinge: module.doorHinge === "left" ? "left" : "right",
    // Откидные фасады вверх или вниз — у антресоли с распашными дверями (09.10).
    doorOpening: module.type === "mezzanine" && door === "hinged" && (module.doorOpening === "up" || module.doorOpening === "down")
      ? module.doorOpening : undefined,
    doorLift: isLiftType(module.doorLift) ? module.doorLift : undefined,
    facade,
    facadePattern,
    // Две стороны покраски бывают только у МДФ в эмали.
    facadePaintSides: module.facadePaintSides === "two" && facade === "satin" ? "two" as const : undefined,
    facadeMaterial,
    facadeColor,
    facadeColorHex,
    facadeColorCode,
    // Ручка должна подходить к фасаду: у стекла — зацеп в профиле или Tip-On,
    // фрезерованная — только у МДФ.
    handle: handleForFacade(handle, facade),
    sideMirrors: normalizeSideMirrors(module),
    backDecor: normalizeBackDecor(module),
    // Фасад с антресолью всегда раздельный (решение Сергея 28.09): у корпуса и
    // антресоли свои створки. Старые проекты с единым фасадом становятся раздельными.
    mezzanineFacade: undefined,
    cabinetToCeiling: module.cabinetToCeiling === true && cabinetSupportsToCeiling(module) ? true as const : undefined,
    // Наружные ящики внизу: хранится запрошенное число (0–4), сколько реально
    // поместится, считает cabinetDrawerCount — при возврате высоты выбор вернётся.
    cabinetDrawers: supportsCabinetDrawers(module) && Number(module.cabinetDrawers) > 0
      ? Math.min(CABINET_DRAWER_MAX_COUNT, Math.round(Number(module.cabinetDrawers)))
      : undefined,
    cabinetDrawerHeight: supportsCabinetDrawers(module) && module.cabinetDrawerHeight !== undefined
      ? cabinetDrawerFrontHeightMm(module)
      : undefined,
    decorDensity: (["minimal", "normal", "full"] as string[]).includes(
      module.decorDensity as string
    )
      ? module.decorDensity
      : "normal",
    fillingPreset: (
      [
        "doubleHang",
        "longHang",
        "shelvesDrawers",
        "shoesAccessories",
        "mixed",
        "empty"
      ] as string[]
    ).includes(module.fillingPreset as string)
      ? module.fillingPreset
      : undefined,
    baseType: cabinetBaseType(module),
    baseHeight: cabinetBaseHeightMm(module),
    baseSetback: cabinetBaseSetbackMm(module),
    fillerLeft: leftFillerMm(module),
    fillerRight: rightFillerMm(module),
    fillerTop: topFillerMm(module),
    height,
    depth
  } as WardrobeModule;
  normalized.width = clamp(
    Math.round(module.width),
    module.type === "corner45" ? 300 : 300,
    maxModuleWidth(normalized)
  );
  if (module.type === "corner45") {
    // Угловой по картинке Сергея (28.10): один корпус, уходит вглубь угла за
    // соседний шкаф у другой стены; добор и одна дверь с петлями у наружного
    // торца. Поля Г-образного и пятигранного (корпус Б, колонка, ящики, петли
    // у угла) больше не нужны — старые проекты открываются новым угловым.
    const legacy = normalized as unknown as Record<string, unknown>;
    for (const key of ["cornerDoorCount", "cornerColumn", "cornerColumnWidth", "cornerColumnShelves",
      "cornerColumnShelfLevels", "cornerColumnDrawers", "cornerHingeA", "cornerHingeB"]) delete legacy[key];
    normalized.cornerSide = cornerSide(module);
    normalized.cornerRun = module.cornerRun === "side" ? "side" : undefined;
    normalized.cornerDoorWidth = cornerDoorWidthMm(module);
    normalized.cornerFiller = cornerFillerMm(module);
    normalized.cornerNeighbourFiller = module.door === "open" ? undefined : cornerNeighbourFillerMm(module);
    normalized.cornerBlind = Number.isFinite(module.cornerBlind)
      ? clamp(Math.round(module.cornerBlind!), CORNER_BLIND_MIN_MM, CORNER_BLIND_MAX_MM) : undefined;
    normalized.cornerMezzanine = cornerMezzanineMm(module) || undefined;
    const arms = cornerArms(normalized);
    normalized.width = arms.a;
    normalized.cornerReturnWidth = arms.b;
    if (normalized.door !== "open") normalized.door = "hinged";
    normalized.cabinetDrawers = undefined;
    normalized.fillerLeft = 0;
    normalized.fillerRight = 0;
    normalized.fillerTop = 0;
    normalized.fillingPreset = undefined;
  }
  // Полки и ящики в своём декоре, свой фасад у отдельных створок (16.10).
  normalized.shelfDecor = normalizeShelfDecor(normalized);
  normalized.drawerDecor = normalizeDrawerDecor(normalized);
  normalized.doorFacades = normalizeDoorFacades(normalized);
  const handleDoorWidth =
    module.type === "corner45"
      ? cornerDoorWidthMm(normalized)
      : normalized.width / Math.max(1, doorLeafCount(normalized));
  // Над наружными ящиками створка короче: ручка считается от её нижней кромки.
  const handleDoorHeight = handleDoorHeightMm(normalized);
  // Раньше центр ручки по умолчанию считался от двери на всю высоту и
  // сохранялся числом — над ящиками ручка уезжала к верхней кромке. Такое
  // значение по умолчанию пересчитываем от настоящей створки.
  if (
    cabinetDrawerLayout(normalized) &&
    normalized.handleCenterY === Math.round(Math.max(120, normalized.height - cabinetBaseHeightMm(normalized)) * 0.52)
  ) {
    normalized.handleCenterY = undefined;
  }
  normalized.handleOrientation = handleOrientation(normalized);
  normalized.integratedHandleStyle = normalized.handle === "integrated" &&
    (integratedHandleStyle(normalized) === "edgePart" || integratedHandleStyle(normalized) === "top")
    ? integratedHandleStyle(normalized) : undefined;
  // У зацепа и Tip-On длины нет: не храним 0, иначе отдельная створка с
  // обычной ручкой (свой фасад, 16.10) получала ручку 64 мм (приёмка 23.10).
  normalized.handleLength = normalized.handle === "push" || normalized.handle === "glassGrip"
    ? (Number(module.handleLength) > 0 ? Math.round(Number(module.handleLength)) : undefined)
    : handleLengthMm(normalized, handleDoorWidth, handleDoorHeight);
  normalized.handleCenterY = handleCenterYmm(
    normalized,
    handleDoorWidth,
    handleDoorHeight
  );
  normalized.handleEdgeOffset = handleEdgeOffsetMm(
    normalized,
    handleDoorWidth,
    handleDoorHeight
  );
  // Подсветка шкафа (замечание № 13): старая «LED-полка» становится полкой со
  // своей лентой, её цвет света — общим цветом подсветки шкафа.
  const lightingSupported = cabinetSupportsLighting(normalized);
  const legacyLed = module.items.find((item) => item.type === "led");
  normalized.cabinetUprightLight = lightingSupported && module.cabinetUprightLight ? true : undefined;
  normalized.cabinetLightTemperature = module.cabinetLightTemperature === "cold" ||
    (!module.cabinetLightTemperature && legacyLed?.lightTemperature === "cold") ? "cold" : undefined;
  normalized.cabinetLightPosition = module.cabinetLightPosition === "back" ? "back" : undefined;
  normalized.hingeColor = module.hingeColor === "black" ? "black" : undefined;
  normalized.edgeFinish = normalizeEdgeFinish(module.edgeFinish);
  // Детали, которые оказались ниже дна (поставили цокольные ящики), не
  // прилипают к ящикам, а встают равномерно в свободное место над ними —
  // до ближайшей детали выше (приёмка 11.10, wf-09).
  const pushedPlacement = new Map<string, number>();
  if (module.type !== "corner45") {
    const sinks = module.items.filter((item) => item.type !== "divider" && Number.isFinite(item.y));
    const pushed = sinks.filter((item) => item.y < internalItemMinimumY(normalized, item) - 0.5).sort((a, b) => a.y - b.y);
    if (pushed.length) {
      const floor = Math.max(...pushed.map((item) => internalItemMinimumY(normalized, item)));
      const above = sinks.filter((item) => !pushed.includes(item) && item.y > floor).map((item) => item.y);
      const ceiling = Math.min(height - 80, ...above);
      const step = (ceiling - floor) / (pushed.length + 1);
      if (step >= 64) pushed.forEach((item, index) => pushedPlacement.set(item.id, floor + step * (index + 1)));
    }
  }
  normalized.items = module.items.filter(item => module.type !== "corner45" || item.type === "shelf" || item.type === "rail").map((original) => {
    const lit = lightingSupported && !normalized.cabinetUprightLight &&
      (original.type === "led" || (original.type === "shelf" && original.light === true));
    const { lightTemperature: _legacyTemperature, light: _light, ...plain } = original;
    void _legacyTemperature; void _light;
    const source: InternalItem = original.type === "led" || original.type === "shelf"
      ? { ...plain, type: "shelf", ...(lit ? { light: true } : {}) }
      : original.type === "shoeRack" && original.height !== undefined
        // Старая металлическая обувница (ярусы высотой item.height под отметкой)
        // стала деревянной полкой с бортиком: полка встаёт на низ её места.
        ? (({ height, ...shoe }) => ({ ...shoe, y: shoe.y - height + 9 }))(original as InternalItem & { height: number })
        : original;
    // Отсек детали — только число и только у полок, ящиков и прочего, не у перегородок.
    const { anchorX, ...rest } = source;
    const item: InternalItem = source.type !== "divider" && Number.isFinite(anchorX)
      ? { ...rest, anchorX: Math.round(Number(anchorX)) }
      : rest;
    // Keep the original level order for the corner packing pass below. Clamping
    // several high levels to the same ceiling first would lose that order.
    if (module.type === "corner45") return { ...item, y: Number.isFinite(item.y) ? item.y : 128 };
    if (item.type !== "divider") {
      const minimumY = internalItemMinimumY(normalized, item);
      // Дважды: упёршаяся в край высота может быть вне шага 32 мм, и второй
      // проход её бы округлил — нормализация должна быть устойчивой (проверка 07.10).
      const place = (y: number) => clamp(snap(y), minimumY, Math.max(minimumY, height - 80));
      return {
        ...item,
        y: place(place(pushedPlacement.get(item.id) ?? item.y))
      };
    }

    const sourceDividerHeight =
      item.height ?? Math.min(1400, height - 180);
    const resizedDivider = resizeDividerFromBottom(
      height,
      item.y,
      sourceDividerHeight,
      sourceDividerHeight
    );
    // Перегородка может занять всю высоту внутри: от дна над цоколем до крыши.
    const dividerLimits = dividerSpanLimits(normalized);
    const dividerHeight = clamp(
      resizedDivider.height,
      320,
      Math.max(320, dividerLimits.top - dividerLimits.bottom)
    );
    const minY = internalItemMinimumY(normalized, {
      ...item,
      height: dividerHeight
    });
    const maxY = Math.max(minY, height - CARCASS_MM - dividerHeight / 2);
    return {
      ...item,
      x: clampDividerX(normalized, item.x ?? 0),
      y: clamp(resizedDivider.bottom + dividerHeight / 2, minY, maxY),
      height: dividerHeight
    };
  });
  // Перегородка — от опоры до опоры (приёмка 22.10): после полок и ящиков
  // прижимаем её края к ближайшим дну, полкам и крыше. Полку убрали или
  // сдвинули — перегородка встаёт по новым опорам, а не висит в воздухе.
  if (module.type !== "corner45") {
    normalized.items = normalized.items.map((item) => {
      if (item.type !== "divider") return item;
      const span = fitDividerToSupports(normalized, item);
      return span ? { ...item, y: span.y, height: span.height } : item;
    });
  }
  if (module.type === "corner45") {
    // Штанги углового — в корпусе А вдоль задней стены; стена больше не выбирается.
    normalized.items = normalized.items.map(({ cornerRailWall: _wall, ...item }) => item);
    normalized.items = fitCornerItems(normalized) ?? normalized.items;
  }
  return normalized;
};

/**
 * Versions before 2026-09-20 represented one wide cabinet as several adjacent
 * modules sharing cabinetRunId. Collapse those persisted runs into one carcass;
 * the shared internal uprights are now derived from its total width.
 */
const collapseLegacyCabinetRuns = (project: PlannerProject) => {
  const groups = new Map<string, WardrobeModule[]>();
  project.modules.forEach((module) => {
    if (
      !module.cabinetRunId ||
      module.stackedOnId ||
      module.type === "mezzanine" ||
      module.type === "shelving" ||
      module.type === "end" ||
      module.type === "corner45"
    ) return;
    const group = groups.get(module.cabinetRunId) ?? [];
    group.push(module);
    groups.set(module.cabinetRunId, group);
  });

  groups.forEach((unsortedBases, runId) => {
    const bases = [...unsortedBases].sort(
      (first, second) => (first.wallOffset ?? 0) - (second.wallOffset ?? 0)
    );
    const primary = bases[0];
    if (!primary) return;
    const uppers = project.modules.filter(
      (module) =>
        module.type === "mezzanine" &&
        (module.cabinetRunId === runId ||
          bases.some((base) => base.id === module.stackedOnId))
    );

    if (bases.length === 1) {
      delete primary.cabinetRunId;
      uppers.forEach((upper) => delete upper.cabinetRunId);
      return;
    }

    const width = bases.reduce((sum, base) => sum + base.width, 0);
    const startOffset = Math.min(
      ...bases.map((base) => base.wallOffset ?? Infinity)
    );
    const retainedUpper =
      uppers.find((upper) => upper.stackedOnId === primary.id) ?? uppers[0];
    const removedIds = new Set<string>([
      ...bases.slice(1).map((base) => base.id),
      ...uppers.filter((upper) => upper.id !== retainedUpper?.id).map((upper) => upper.id)
    ]);

    Object.assign(
      primary,
      normalizeDecor(normalizeModule({
        ...primary,
        cabinetRunId: undefined,
        type: width <= 600 ? "single" : "double",
        width,
        wallOffset: Number.isFinite(startOffset) ? startOffset : primary.wallOffset,
        fillerLeft: bases[0]?.fillerLeft,
        fillerRight: bases.at(-1)?.fillerRight
      }))
    );
    delete primary.cabinetRunId;

    if (retainedUpper) {
      retainedUpper.stackedOnId = primary.id;
      retainedUpper.wallOffset = primary.wallOffset;
      retainedUpper.width = width;
      retainedUpper.depth = primary.depth;
      retainedUpper.fillerLeft = primary.fillerLeft;
      retainedUpper.fillerRight = primary.fillerRight;
      delete retainedUpper.cabinetRunId;
    }

    project.modules.forEach((module) => {
      if (module.endAttachedToId && removedIds.has(module.endAttachedToId)) {
        module.endAttachedToId = primary.id;
      }
    });
    if (
      project.selectedModuleId &&
      removedIds.has(project.selectedModuleId)
    ) {
      project.selectedModuleId = primary.id;
      project.selectedItemId = null;
      project.selectedShelvingCellId = null;
    }
    project.modules = project.modules.filter(
      (module) => !removedIds.has(module.id)
    );
  });
};

/** Проект конструктора в браузере. Страница модели на сайте хранит свой — EGEL_STORE_KEY (12.10). */
export const PLANNER_STORAGE_KEY = "egel-planner-project";
export const PLANNER_STORE_VERSION = 20;
const plannerStorageKey = () => {
  const key = (globalThis as { EGEL_STORE_KEY?: unknown }).EGEL_STORE_KEY;
  return typeof key === "string" && key ? key : PLANNER_STORAGE_KEY;
};

/** Шкафы, у которых фасад можно сделать одинаковым (без стеллажей, комодов и торцов). */
const FACADE_SYNC_TYPES = new Set<WardrobeModule["type"]>(["single", "double", "corner45"]);
/** Отделка фасада шкафа: то, что переносится кнопкой «Применить ко всем шкафам». */
const facadeFinishOf = (module: WardrobeModule): Partial<WardrobeModule> => ({
  door: module.door,
  facade: module.facade,
  facadePattern: module.facadePattern,
  facadeMaterial: module.facadeMaterial,
  facadeDecor: module.facadeDecor,
  facadeColor: module.facadeColor,
  facadeColorHex: module.facadeColorHex,
  facadeColorCode: module.facadeColorCode,
  facadePaintSides: module.facadePaintSides,
  handle: module.handle,
  integratedHandleStyle: module.integratedHandleStyle
});

/**
 * Угловой и сосед перед его глухой частью — одно изделие (29.10): меняешь у
 * одного корпус, кромку, фасад, ручку, петли или цоколь — второй меняется сам.
 */
const CORNER_LINKED_KEYS = [
  "decor", "material", "edgeFinish", "baseType", "baseHeight", "baseSetback", "hingeColor",
  "facade", "facadePattern", "facadeMaterial", "facadeDecor", "facadeColor", "facadeColorHex", "facadeColorCode",
  "facadePaintSides", "handle", "integratedHandleStyle"
] as const satisfies readonly (keyof WardrobeModule)[];

/** Пара углового: сосед углового или угловой соседа; null — пары нет. */
export const cornerPartner = (project: PlannerProject, module: WardrobeModule) => {
  if (module.type === "corner45") {
    const neighbour = cornerNeighbour(project, module);
    return neighbour && (neighbour.type === "single" || neighbour.type === "double") ? neighbour : null;
  }
  if (module.type !== "single" && module.type !== "double") return null;
  return project.modules.find((candidate) => candidate.type === "corner45" &&
    cornerNeighbour(project, candidate)?.id === module.id) ?? null;
};

/** Корпус шкафа — то, что переносит «Применить этот корпус ко всем шкафам». */
const CARCASS_FINISH_KEYS = ["decor", "material", "edgeFinish", "baseType", "baseHeight", "baseSetback"] as const satisfies readonly (keyof WardrobeModule)[];
const CARCASS_SYNC_TYPES = new Set<WardrobeModule["type"]>(["single", "double", "corner45"]);

const pickKeys = <K extends keyof WardrobeModule>(source: Partial<WardrobeModule>, keys: readonly K[]) => {
  const picked: Partial<WardrobeModule> = {};
  for (const key of keys) if (Object.prototype.hasOwnProperty.call(source, key)) picked[key] = source[key];
  return picked;
};

/** Декор корпуса — у шкафа, его торцов и антресоли над ним. */
const applyCarcassDecor = (project: PlannerProject, module: WardrobeModule, code: string, material: WardrobeModule["material"]) => {
  module.decor = code;
  module.material = material;
  module.shelvingColorHex = undefined;
  module.shelvingColorCode = undefined;
  module.carcassEnamelHex = undefined;
  module.carcassEnamelCode = undefined;
  if (module.type !== "end" && module.type !== "mezzanine") {
    syncAttachedEndModuleMaterials(project, module.id, material);
    project.modules.filter(item => item.endAttachedToId === module.id).forEach(item => { item.decor = code; });
  }
  if (!module.stackedOnId) {
    project.modules.filter(item => item.stackedOnId === module.id).forEach(item => {
      item.decor = code;
      item.material = material;
      item.shelvingColorHex = undefined;
      item.shelvingColorCode = undefined;
      item.carcassEnamelHex = undefined;
      item.carcassEnamelCode = undefined;
    });
  }
};

export const normalizeProject = (project: PlannerProject): PlannerProject => {
  const persistedRoom = project.room as Partial<RoomSettings>;
  const roomValues = {
    ...initialProject.room,
    ...persistedRoom
  };
  const style = isRoomStyle(persistedRoom.style)
    ? persistedRoom.style
    : inferRoomStyle(roomValues);
  const room: RoomSettings = {
    style,
    wallColor: roomValues.wallColor,
    floor: roomValues.floor,
    lighting: roomValues.lighting,
    ceilingHeight: ceilingHeightMm({ ...project, room: roomValues }),
    ...(roomValues.width !== undefined ? { width: roomWidthMm({ room: roomValues }) } : {}),
    ...(persistedRoom.ceilingGap !== undefined && ceilingGapMm({ room: roomValues }) !== DEFAULT_CEILING_GAP_MM
      ? { ceilingGap: ceilingGapMm({ room: roomValues }) } : {})
  };
  // Ширина комнаты влияет на все расчёты раскладки ниже.
  applyRoomSize({ room });
  const number = PROJECT_NUMBER_PATTERN.test(String(project.number ?? ""))
    ? String(project.number)
    : createProjectNumber();
  const families = projectProductFamilies(project);
  const requestedName = String(project.name ?? "").trim().slice(0, 60);
  const normalized: PlannerProject = {
    ...project,
    number,
    // Пока клиент не дал своё название, оно собирается из номера и изделия.
    name: isAutoProjectName(requestedName, number)
      ? autoProjectName(number, families.length === 1 ? families[0]! : null)
      : requestedName,
    carcassDecor: resolveDecor(project.carcassDecor, project.carcassMaterial).code,
    selectedShelvingCellId: project.selectedShelvingCellId ?? null,
    room,
    modules: project.modules.map(module => normalizeDecor(normalizeModule(module)))
  };
  collapseLegacyCabinetRuns(normalized);
  const ceilingHeight = usableRoomHeightMm(normalized);
  const assignedBases = new Set<string>();

  normalized.modules.forEach((mezzanine, index) => {
    if (mezzanine.type !== "mezzanine") return;
    let base = normalized.modules.find(
      (candidate) =>
        candidate.id === mezzanine.stackedOnId &&
        candidate.type !== "mezzanine" &&
        candidate.type !== "corner45" &&
        candidate.type !== "shelving" &&
        candidate.type !== "end" &&
        !assignedBases.has(candidate.id)
    );
    if (!base) {
      base = [...normalized.modules]
        .slice(0, index)
        .reverse()
        .find(
          (candidate) =>
            candidate.type !== "mezzanine" &&
            candidate.type !== "corner45" &&
            candidate.type !== "shelving" &&
            candidate.type !== "end" &&
            !assignedBases.has(candidate.id)
        );
    }
    if (!base) {
      base = normalized.modules.find(
        (candidate) =>
          candidate.type !== "mezzanine" &&
          candidate.type !== "corner45" &&
          candidate.type !== "shelving" &&
          candidate.type !== "end" &&
          !assignedBases.has(candidate.id)
      );
    }
    if (!base) {
      mezzanine.type = "double";
      delete mezzanine.stackedOnId;
      return;
    }

    assignedBases.add(base.id);
    Object.assign(
      mezzanine,
      normalizeModule({
        ...mezzanine,
        // Фасад антресоли — как у шкафа под ней (видео Сергея 24.10): двери,
        // отделка, цвет, фрезеровка и ручка. Своё у антресоли — только
        // открывание (на петлях или вверх) и сторона петель.
        door: base.door === "open" ? "open" : "hinged",
        facade: base.facade,
        facadePattern: base.facadePattern,
        facadeMaterial: base.facadeMaterial,
        facadeDecor: base.facadeDecor,
        facadeColor: base.facadeColor,
        facadeColorHex: base.facadeColorHex,
        facadeColorCode: base.facadeColorCode,
        facadePaintSides: base.facadePaintSides,
        handle: base.handle,
        integratedHandleStyle: base.integratedHandleStyle,
        hingeColor: base.hingeColor,
        edgeFinish: base.edgeFinish,
        stackedOnId: base.id,
        wallOffset: base.wallOffset,
        width: base.width,
        depth: base.depth,
        baseType: "floor",
        baseHeight: 0,
        baseSetback: base.baseSetback,
        fillerLeft: base.fillerLeft,
        fillerRight: base.fillerRight,
        fillerTop: base.fillerTop,
        height: mezzanine.height
      })
    );
  });

  normalized.modules
    .filter((module) => module.type !== "mezzanine" && !module.stackedOnId)
    .forEach((base) => {
      let mezzanine = mezzanineFor(normalized, base.id);
      const availableHeight = Math.max(
        MIN_MODULE_HEIGHT_MM,
        ceilingHeight - topFillerMm(base)
      );
      // «До потолка»: корпус максимальный, антресоль добирает до потолка —
      // заново при каждой смене потолка или верхнего добора (№ 11).
      if (base.cabinetToCeiling) {
        const split = cabinetToCeilingSplit(availableHeight, Boolean(mezzanine));
        if (split) {
          base.height = split.baseHeight;
          if (split.mezzanineHeight && !mezzanine) {
            mezzanine = createStackedMezzanine(base, split.mezzanineHeight);
            normalized.modules.splice(normalized.modules.indexOf(base) + 1, 0, mezzanine);
          }
          if (mezzanine) mezzanine.height = split.mezzanineHeight;
        }
      }
      if (!mezzanine) {
        Object.assign(
          base,
          normalizeModule({
            ...base,
            height: Math.min(base.height, availableHeight)
          })
        );
        return;
      }

      const baseHeight = Math.min(
        base.height,
        availableHeight - MIN_MODULE_HEIGHT_MM
      );
      const mezzanineHeight = Math.min(
        mezzanine.height,
        availableHeight - baseHeight
      );
      Object.assign(
        base,
        normalizeModule({ ...base, height: baseHeight })
      );
      Object.assign(
        mezzanine,
        normalizeModule({
          ...mezzanine,
          stackedOnId: base.id,
          wallOffset: base.wallOffset,
          width: base.width,
          depth: base.depth,
          baseType: "floor",
          baseHeight: 0,
          baseSetback: base.baseSetback,
          fillerLeft: base.fillerLeft,
          fillerRight: base.fillerRight,
          fillerTop: base.fillerTop,
          height: mezzanineHeight
        })
      );
    });

  const assignedShelvingBases = new Set<string>();
  const removedShelvingUppers = new Set<string>();
  normalized.modules.forEach((upper) => {
    if (upper.type !== "shelving" || !upper.stackedOnId) return;
    const base = normalized.modules.find(
      candidate =>
        candidate.id === upper.stackedOnId &&
        candidate.type === "shelving" &&
        !candidate.stackedOnId &&
        !assignedShelvingBases.has(candidate.id)
    );
    if (!base) {
      delete upper.stackedOnId;
      return;
    }
    assignedShelvingBases.add(base.id);
    const availableHeight = Math.min(
      SHELVING_MAX_HEIGHT_MM + SHELVING_MAX_MEZZANINE_HEIGHT_MM,
      ceilingHeight - topFillerMm(base)
    );
    // Антресоль выше 600 мм (старые проекты) урезается, нижний стеллаж остаётся прежним.
    const split = shelvingHeightSplit(
      Math.min(base.height + Math.min(upper.height, SHELVING_MAX_MEZZANINE_HEIGHT_MM), availableHeight),
      base.height
    );
    Object.assign(
      base,
      normalizeModule({ ...base, height: split.baseHeight })
    );
    if (!split.mezzanineHeight) {
      removedShelvingUppers.add(upper.id);
      return;
    }
    Object.assign(
      upper,
      normalizeDecor(normalizeModule({
        ...upper,
        stackedOnId: base.id,
        wallOffset: base.wallOffset,
        width: base.width,
        height: split.mezzanineHeight,
        depth: base.depth,
        shelvingBase: "floor"
      }))
    );
  });
  if (removedShelvingUppers.size) {
    normalized.modules = normalized.modules.filter(
      module => !removedShelvingUppers.has(module.id)
    );
  }

  alignEndModuleOffsets(normalized);
  if (separateOverlappingFloorModules(normalized)) {
    alignEndModuleOffsets(normalized);
  }
  // Антресоль стоит там же, где её корпус: своё смещение у неё только копия.
  // Устаревшая копия (после переноса стены или раздвигания) делала нормализацию
  // неустойчивой — второй проход менял проект (проверка 07.10).
  normalized.modules.forEach((module) => {
    if (!module.stackedOnId) return;
    const base = normalized.modules.find((candidate) => candidate.id === module.stackedOnId);
    if (base && Number.isFinite(base.wallOffset)) module.wallOffset = base.wallOffset;
    else delete module.wallOffset;
  });

  // Глухая часть углового — по глубине соседнего шкафа с дверью (28.10): сосед
  // стал глубже или мельче — угловой подстраивается сам. Антресоль у соседа —
  // у углового такая же и на той же высоте (картинка Сергея, п. 5).
  normalized.modules.forEach((corner) => {
    if (corner.type !== "corner45") return;
    const neighbour = cornerNeighbour(normalized, corner);
    const neighbourMezzanine = neighbour ? mezzanineFor(normalized, neighbour.id) : undefined;
    if (neighbour && neighbourMezzanine) {
      corner.height = neighbour.height;
      corner.cornerMezzanine = Math.min(CORNER_MEZZANINE_MAX_MM, Math.max(CORNER_MEZZANINE_MIN_MM, neighbourMezzanine.height));
    }
    const blind = neighbour ? cornerBlindFor(neighbour) : undefined;
    if (blind === corner.cornerBlind) return;
    corner.cornerBlind = blind;
    const arms = cornerArms(corner);
    corner.width = arms.a;
    corner.cornerReturnWidth = arms.b;
  });

  if (
    normalized.selectedModuleId &&
    !normalized.modules.some((module) => module.id === normalized.selectedModuleId)
  ) {
    normalized.selectedModuleId = null;
    normalized.selectedItemId = null;
    normalized.selectedShelvingCellId = null;
  }
  if (normalized.selectedShelvingCellId) {
    const selectedCell = parseShelvingCellId(normalized.selectedShelvingCellId);
    const owner = selectedCell
      ? normalized.modules.find((module) => module.id === selectedCell.moduleId)
      : undefined;
    if (
      !selectedCell ||
      !owner ||
      owner.type !== "shelving" ||
      selectedCell.column < 0 ||
      selectedCell.column >= (owner.shelvingColumns ?? 0) ||
      selectedCell.row < 0 ||
      selectedCell.row >= (owner.shelvingRows ?? 0)
    ) {
      normalized.selectedShelvingCellId = null;
    }
  }
  return normalized;
};

export const usePlannerStore = create<PlannerState>()(
  persist(
    (set) => {
      const commit = (mutate: (draft: PlannerProject) => void | boolean) =>
        set((state) => {
          const draft = clone(state.project);
          if (mutate(draft) === false) return state;
          const normalized = normalizeProject(draft);
          return {
            project: normalized,
            past: [...state.past.slice(-29), clone(state.project)],
            future: []
          };
        });

      return {
        project: normalizeProject(clone(initialProject)),
        addCatalogShelf: (module) => set(state => ({
          project: normalizeProject(appendCatalogShelf(state.project, module)),
          past: [...state.past.slice(-29), clone(state.project)], future: []
        })),
        replaceWithCatalogShelf: (module) => set(state => ({
          // Нормализация, как после любой правки: название сразу получает изделие.
          project: normalizeProject(replaceWithCatalogShelf(state.project, module)),
          past: [...state.past.slice(-29), clone(state.project)], future: []
        })),
        replaceWithCatalogComposition: (modules) => set(state => ({
          project: normalizeProject(replaceWithCatalogComposition(state.project, modules)),
          past: [...state.past.slice(-29), clone(state.project)], future: []
        })),
        past: [],
        future: [],

        selectModule: (id) =>
          set((state) => ({
            project: {
              ...state.project,
              selectedModuleId: id,
              selectedItemId: null,
              selectedShelvingCellId: null
            }
          })),

        clearSelection: () =>
          set((state) => ({
            project: {
              ...state.project,
              selectedModuleId: null,
              selectedItemId: null,
              selectedShelvingCellId: null
            }
          })),

        selectItem: (moduleId, itemId) =>
          set((state) => ({
            project: {
              ...state.project,
              selectedModuleId: moduleId,
              selectedItemId: itemId,
              selectedShelvingCellId: null
            }
          })),

        selectShelvingCell: (moduleId, cellId) =>
          set((state) => ({
            project: {
              ...state.project,
              selectedModuleId: moduleId,
              selectedItemId: null,
              selectedShelvingCellId: cellId
            }
          })),

        addModule: (type, options = {}) =>
          commit((project) => {
            if (type === "corner45") {
              const side = options.cornerSide === "left" ? "left" : "right";
              if (cornerAddBlocker(project, side)) return false;
              // Preserve already positioned products when the first corner is added.
              const offsets = materializeWallOffsets(project);
              project.modules.forEach(module => { module.wallOffset = offsets.get(module.id); });
            }
            if (type === "mezzanine") {
              const selected = project.modules.find(
                (module) => module.id === project.selectedModuleId
              );
              const base = selected?.type === "mezzanine" ? undefined : selected;
              const availableHeight = base
                ? usableRoomHeightMm(project) - base.height - topFillerMm(base)
                : 0;
              if (
                !base ||
                base.type === "corner45" ||
                base.type === "shelving" ||
                base.type === "end" ||
                mezzanineFor(project, base.id) ||
                availableHeight < MIN_MODULE_HEIGHT_MM
              ) {
                return;
              }
              const module: WardrobeModule = {
                id: createId("module"),
                type: "mezzanine",
                stackedOnId: base.id,
                wallOffset: base.wallOffset,
                width: base.width,
                height: Math.min(560, availableHeight),
                depth: base.depth,
                material: base.material ?? project.carcassMaterial,
                door: base.door,
                doorHinge: base.doorHinge,
                facade: base.facade,
                facadePattern: base.facadePattern,
                facadeMaterial: base.facadeMaterial,
                facadeColor: base.facadeColor,
                facadeColorHex: base.facadeColorHex,
                facadeColorCode: base.facadeColorCode,
                handle: base.handle,
                handleLength: base.handleLength,
                handleCenterY: base.handleCenterY,
                handleEdgeOffset: base.handleEdgeOffset,
                handleOrientation: base.handleOrientation,
                integratedHandleStyle: base.integratedHandleStyle,
                baseType: "floor",
                baseHeight: 0,
                baseSetback: base.baseSetback,
                fillerLeft: base.fillerLeft,
                fillerRight: base.fillerRight,
                fillerTop: base.fillerTop,
                decorDensity: base.decorDensity ?? "normal",
                items: []
              };
              const baseIndex = project.modules.findIndex(
                (candidate) => candidate.id === base.id
              );
              project.modules.splice(baseIndex + 1, 0, module);
              project.selectedModuleId = module.id;
              project.selectedItemId = null;
              project.selectedShelvingCellId = null;
              return;
            }
            const requestedEndSide =
              options.endSide === "left" ? "left" as const : "right" as const;
            const requestedEndTarget =
              type === "end"
                ? endModuleAddTarget(project, requestedEndSide)
                : undefined;
            if (
              type === "end" &&
              (!requestedEndTarget ||
                endModuleForTargetSide(project, requestedEndTarget.id, requestedEndSide))
            ) {
              return false;
            }
            const previous = project.modules.at(-1);
            const module: WardrobeModule = {
              id: createId("module"),
              type,
              width:
                type === "single"
                  ? 500
                  : type === "shelving"
                    ? 1600
                    : 900,
              height: Math.min(
                type === "shelving" ? 2200 : 2360,
                usableRoomHeightMm(project)
              ),
              depth:
                type === "shelving"
                  ? 350
                  : previous?.type === "shelving"
                    ? 580
                    : previous?.depth ?? 580,
              material: previous?.material ?? project.carcassMaterial,
              door: "open",
              doorHinge: "right",
              facade: "wood",
              facadePattern: previous?.facadePattern ?? "plain",
              facadeMaterial:
                previous?.facadeMaterial ??
                previous?.material ??
                project.carcassMaterial,
              facadeColor: previous?.facadeColor ?? "white",
              facadeColorHex: previous?.facadeColorHex,
              facadeColorCode: previous?.facadeColorCode,
              handle: "bar",
              baseType: "plinth",
              baseHeight: DEFAULT_BASE_HEIGHT_MM,
              baseSetback: DEFAULT_BASE_SETBACK_MM,
              fillerLeft: 0,
              fillerRight: 0,
              fillerTop: 0,
              decorDensity: "normal",
              items: [],
              ...(type === "shelving"
                ? {
                    shelvingColumns: 4,
                    shelvingRows: 5,
                    shelvingColumnRatios: [0.25, 0.25, 0.25, 0.25],
                    shelvingRowRatios: [0.2, 0.2, 0.2, 0.2, 0.2],
                    shelvingBase: "plinth" as const,
                    shelvingBackPanel: false
                  }
                : type === "corner45"
                  ? {
                      // Угловой (28.10): у задней стены, дверь 600, доборы углового и
                      // соседа по 50 (31.10); глухая часть — по соседнему шкафу.
                      // Полка и штанга вглубь угла. Плечи пересчитает нормализация.
                      width: 1250,
                      cornerReturnWidth: 653,
                      cornerDoorWidth: 600,
                      cornerFiller: 50,
                      cornerNeighbourFiller: 50,
                      cornerSide: options.cornerSide === "left" ? "left" as const : "right" as const,
                      depth: 580,
                      door: "hinged" as const,
                      items: [
                        { id: createId("item"), type: "shelf" as const, y: 1856 },
                        { id: createId("item"), type: "rail" as const, y: 1760 }
                      ]
                    }
                : type === "end"
                  ? {
                      width: 320,
                      // Глубина — как у корпуса, к которому торец крепится, а не у последнего добавленного.
                      depth: requestedEndTarget?.depth ?? previous?.depth ?? 580,
                      door: "open" as const,
                      handle: "push" as const,
                      endSide: requestedEndSide,
                      endShape: options.endShape === "beveled" ? "beveled" as const : "rounded" as const,
                      endShelfCount: clamp(
                        Math.round(options.endShelfCount ?? 5),
                        END_MODULE_MIN_TIERS,
                        END_MODULE_MAX_TIERS
                      )
                    }
                  : {}),
              ...options
            };
            if (type === "end") {
              const adjacentModule = requestedEndTarget;
              if (options.material === undefined && adjacentModule) {
                module.material =
                  adjacentModule.material ?? project.carcassMaterial;
              }
              module.endAttachedToId = adjacentModule?.id;
              if (adjacentModule) {
                if (!canAttachEndModule(project, module, adjacentModule, requestedEndSide)) {
                  return false;
                }
                module.wallOffset = endModuleAttachmentOffset(
                  project,
                  module,
                  adjacentModule,
                  requestedEndSide
                );
              }
            }
            if (type === "end" && module.endSide === "left") {
              project.modules.unshift(module);
            } else {
              project.modules.push(module);
            }
            // Новый шкаф или стеллаж встаёт в первое свободное место; если у стен
            // места нет — не добавляется, чтобы не стоять внахлёст (проверка 07.10).
            if ((type === "single" || type === "double" || type === "shelving") && moduleOverlapsFloor(project, module.id)) {
              const autoStart = materializeWallOffsets(project).get(module.id) ?? 0;
              const placed = placeModuleGroup(project, module.id, autoStart, 1);
              if (placed === null) return false;
              module.wallOffset = placed;
            }
            project.selectedModuleId = module.id;
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;
          }),

        moveModule: (id, wallOffset) =>
          set((state) => {
            const project = clone(state.project);
            const offsets = materializeWallOffsets(project);
            project.modules.forEach((module) => {
              module.wallOffset = offsets.get(module.id);
            });
            const selected = project.modules.find((candidate) => candidate.id === id);
            const module = baseModuleFor(project, selected);
            if (!module) return state;
            // Шкаф тащат вместе с его торцами: место ищется для всей группы,
            // чтобы торец не налезал на соседа и не отрывался от шкафа (приёмка 07.10, wb-24).
            // Та же функция считает предпросмотр в сцене.
            const resolved = resolveModuleGroupWallOffset(
              project,
              module.id,
              wallOffset,
              wallOffset - (module.wallOffset ?? wallOffset)
            );
            if (Math.abs((module.wallOffset ?? 0) - resolved) < 0.5) return state;
            module.wallOffset = resolved;
            project.modules.sort(
              (a, b) => (a.wallOffset ?? 0) - (b.wallOffset ?? 0)
            );
            project.selectedModuleId = id;
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;

            return {
              project: normalizeProject(project),
              past: [...state.past.slice(-29), clone(state.project)],
              future: []
            };
          }),

        removeSelectedModule: () =>
          commit((project) => {
            const selected = project.modules.find(
              (module) => module.id === project.selectedModuleId
            );
            if (!selected) return;
            const removedIds = new Set<string>();
            removedIds.add(selected.id);
            // Убрали антресоль — шкаф больше не «до потолка», иначе она вернётся.
            if (selected.type === "mezzanine" && selected.stackedOnId) {
              const base = project.modules.find((module) => module.id === selected.stackedOnId);
              if (base) delete base.cabinetToCeiling;
            }
            if (selected.type !== "mezzanine") {
              project.modules
                .filter((module) => module.stackedOnId === selected.id)
                .forEach((module) => removedIds.add(module.id));
            }
            // Торцы крепятся к своему шкафу и уходят вместе с ним, а не
            // перескакивают к соседнему (приёмка 07.10, wb-24).
            if (isEndModuleAttachmentTarget(selected)) {
              project.modules
                .filter((module) => module.type === "end" && module.endAttachedToId === selected.id)
                .forEach((module) => removedIds.add(module.id));
            }
            project.modules = project.modules.filter(
              (module) => !removedIds.has(module.id)
            );
            project.selectedModuleId = selected.stackedOnId &&
              project.modules.some(module => module.id === selected.stackedOnId)
                ? selected.stackedOnId
                : project.modules[0]?.id ?? null;
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;
          }),

        applyFacadeToAllCabinets: (id) => {
          let changed = 0;
          commit((project) => {
            const source = project.modules.find((item) => item.id === id);
            if (!source || !FACADE_SYNC_TYPES.has(source.type)) return false;
            const finish = facadeFinishOf(source);
            project.modules.forEach((module) => {
              if (module.id === source.id || !FACADE_SYNC_TYPES.has(module.type)) return;
              Object.assign(module, finish, { doorFacades: undefined });
              changed += 1;
            });
            return changed > 0;
          });
          return changed;
        },
        applyCarcassToAllCabinets: (id) => {
          let changed = 0;
          commit((project) => {
            const source = project.modules.find((item) => item.id === id);
            if (!source || !CARCASS_SYNC_TYPES.has(source.type)) return false;
            const finish = pickKeys(source, CARCASS_FINISH_KEYS);
            const code = resolveDecor(source.decor ?? project.carcassDecor).code;
            const material = source.material ?? decorPriceMaterial(code);
            project.modules.forEach((module) => {
              if (module.id === source.id || !CARCASS_SYNC_TYPES.has(module.type)) return;
              applyCarcassDecor(project, module, code, material);
              Object.assign(module, { edgeFinish: finish.edgeFinish, baseType: finish.baseType,
                baseHeight: finish.baseHeight, baseSetback: finish.baseSetback });
              changed += 1;
            });
            return changed > 0;
          });
          return changed;
        },
        updateModule: (id, patch) =>
          commit((project) => {
            const module = project.modules.find((item) => item.id === id);
            if (module) {
              if (
                (patch.type === "corner45" && module.type !== "corner45") ||
                patch.type === "mezzanine" ||
                (module.type === "corner45" && patch.type && patch.type !== "corner45") ||
                (patch.type === "end" && module.type !== "end") ||
                (module.type === "mezzanine" && patch.type)
              ) return false;
              // Перенос углового в другой угол — по тому же правилу, что и добавление:
              // занятый угол не занимается, соседи не раздвигаются (проверка 07.10).
              if (module.type === "corner45" && patch.cornerSide && patch.cornerSide !== cornerSide(module) &&
                cornerAddBlocker(project, patch.cornerSide, module)) return false;
              if (module.type === "corner45" && !fitCornerItems({ ...module, ...patch })) return false;
              if (module.type === "end" && (patch.endSide || patch.endAttachedToId)) {
                const nextSide = patch.endSide ?? endModuleSide(module);
                const target = patch.endAttachedToId !== undefined
                  ? project.modules.find(
                      (candidate) =>
                        candidate.id === patch.endAttachedToId &&
                        isEndModuleAttachmentTarget(candidate)
                    )
                  : endModuleTarget(project, module);
                if (
                  !target ||
                  endModuleForTargetSide(project, target.id, nextSide, module.id) ||
                  !canAttachEndModule(
                    project,
                    { ...module, ...patch },
                    target,
                    nextSide
                  )
                ) {
                  return false;
                }
              }
              const doorHeightBefore = handleDoorHeightMm(module);
              // Высоту корпуса или антресоли поменяли руками — «до потолка» выключается.
              if (patch.height !== undefined && !("cabinetToCeiling" in patch)) {
                const base = module.stackedOnId ? project.modules.find((candidate) => candidate.id === module.stackedOnId) : module;
                if (base) delete base.cabinetToCeiling;
              }
              const partner = cornerPartner(project, module);
              Object.assign(module, patch);
              // Пара «угловой + сосед» — в одной отделке (29.10).
              const linked = pickKeys(patch, CORNER_LINKED_KEYS);
              if (partner && Object.keys(linked).length) {
                Object.assign(partner, linked);
                if (Object.keys(linked).some((key) => key.startsWith("facade") || key === "handle")) partner.doorFacades = undefined;
                if (patch.material !== undefined) syncAttachedEndModuleMaterials(project, partner.id, partner.material);
              }
              // Торцы — по глубине своего шкафа и после смены его глубины
              // (проверка 07.10; при добавлении торец уже берёт её).
              if (patch.depth !== undefined && isEndModuleAttachmentTarget(module)) {
                project.modules
                  .filter((candidate) => candidate.type === "end" && candidate.endAttachedToId === module.id)
                  .forEach((end) => {
                    end.depth = clamp(clamp(Math.round(module.depth), 300, 700), END_MODULE_MIN_DEPTH_MM, END_MODULE_MAX_DEPTH_MM);
                  });
              }
              // Наружные ящики поднимают нижнюю кромку двери: ручка остаётся
              // на той же доле высоты створки, а не уезжает к её верху.
              if (
                ("cabinetDrawers" in patch || "cabinetDrawerHeight" in patch) &&
                !("handleCenterY" in patch) &&
                Number.isFinite(module.handleCenterY)
              ) {
                const doorHeightAfter = handleDoorHeightMm(module);
                if (doorHeightAfter !== doorHeightBefore) {
                  module.handleCenterY = Math.round(module.handleCenterY! * doorHeightAfter / doorHeightBefore);
                }
              }
              if (
                module.type === "shelving" &&
                !Object.prototype.hasOwnProperty.call(patch, "shelvingDesign") &&
                patchDivergesFromShelvingDesign(patch)
              ) {
                clearShelvingDesignLink(module);
              }
              if (
                patch.material !== undefined &&
                module.type !== "end" &&
                module.type !== "mezzanine"
              ) {
                syncAttachedEndModuleMaterials(
                  project,
                  module.id,
                  module.material
                );
              }
              if (module.type === "end" && (patch.endSide || patch.endAttachedToId)) {
                project.modules = project.modules.filter(
                  (candidate) => candidate.id !== module.id
                );
                const target = endModuleTarget(project, module);
                module.endAttachedToId = target?.id;
                if (target) module.material = target.material ?? project.carcassMaterial;
                if (target) {
                  module.wallOffset = endModuleAttachmentOffset(
                    project,
                    module,
                    target
                  );
                } else {
                  delete module.wallOffset;
                }
                if (endModuleSide(module) === "left") {
                  project.modules.unshift(module);
                } else {
                  project.modules.push(module);
                }
              }
              if (patch.height !== undefined) {
                module.fillingPreset = undefined;
              }
            }
          }),

        setCabinetTotalHeight: (id, requestedHeight) =>
          commit((project) => {
            const selected = project.modules.find(module => module.id === id);
            const bases = cabinetRunModules(project, selected);
            if (!bases.length) return false;

            for (const base of bases) {
              // Высоту задали руками — «до потолка» выключается.
              delete base.cabinetToCeiling;
              const current = mezzanineFor(project, base.id);
              const availableHeight = Math.min(
                MAX_MODULE_HEIGHT_MM + MAX_MEZZANINE_HEIGHT_MM,
                usableRoomHeightMm(project) - topFillerMm(base)
              );
              const split = cabinetHeightSplit(
                Math.min(Math.round(requestedHeight), availableHeight),
                current ? base.height : undefined
              );
              base.height = split.baseHeight;
              base.fillingPreset = undefined;
              if (split.mezzanineHeight === 0) {
                if (current) {
                  project.modules = project.modules.filter(
                    module => module.id !== current.id
                  );
                }
                continue;
              }
              if (current) {
                current.height = split.mezzanineHeight;
                current.width = base.width;
                current.depth = base.depth;
              } else {
                const mezzanine = createStackedMezzanine(
                  base,
                  split.mezzanineHeight
                );
                const baseIndex = project.modules.findIndex(
                  module => module.id === base.id
                );
                project.modules.splice(baseIndex + 1, 0, mezzanine);
              }
            }
            project.selectedModuleId = bases[0]!.id;
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;
          }),

        setCabinetToCeiling: (id, enabled) =>
          commit((project) => {
            const selected = project.modules.find(module => module.id === id);
            const bases = cabinetRunModules(project, selected).filter(cabinetSupportsToCeiling);
            if (!bases.length) return false;
            if (enabled && !bases.every((base) => cabinetToCeilingSplit(
              usableRoomHeightMm(project) - topFillerMm(base), Boolean(mezzanineFor(project, base.id))))) return false;
            for (const base of bases) {
              if (enabled) {
                base.cabinetToCeiling = true;
                base.fillingPreset = undefined;
              } else {
                delete base.cabinetToCeiling;
              }
            }
            // Сами высоты считает normalizeProject — так же, как при смене потолка.
          }),

        setMezzanineHeight: (id, requestedHeight) =>
          commit((project) => {
            const selected = project.modules.find(module => module.id === id);
            const bases = cabinetRunModules(project, selected);
            let changed = false;
            for (const base of bases) {
              const mezzanine = mezzanineFor(project, base.id);
              if (!mezzanine) continue;
              // Антресоль выше — сначала свободное место до потолка, потом корпус
              // ниже; шкаф до потолка остаётся до потолка (приёмка 22.10).
              const split = mezzanineHeightSplit(project, base, mezzanine.height, requestedHeight);
              if (split.mezzanineHeight === mezzanine.height && split.baseHeight === base.height) continue;
              // Высоты заданы руками — режим «до потолка» выключается, но шкаф,
              // стоявший до потолка, до потолка и остаётся (mezzanineHeightSplit).
              delete base.cabinetToCeiling;
              mezzanine.height = split.mezzanineHeight;
              if (split.baseHeight !== base.height) {
                base.height = split.baseHeight;
                base.fillingPreset = undefined;
              }
              changed = true;
            }
            if (!changed) return false;
          }),

        setCabinetTotalWidth: (id, requestedWidth) =>
          commit((project) => {
            const selected = project.modules.find(module => module.id === id);
            const source = baseModuleFor(project, selected);
            if (
              !source ||
              source.type === "shelving" ||
              source.type === "end" ||
              source.type === "corner45"
            ) return false;

            const currentBases = cabinetRunModules(project, source).sort(
              (first, second) => (first.wallOffset ?? 0) - (second.wallOffset ?? 0)
            );
            const primary = currentBases[0] ?? source;
            // Не шире свободного места на задней стене: иначе шкаф уезжает на боковую стену.
            const width = Math.min(
              Math.max(300, Math.round(requestedWidth)),
              maxCabinetRunWidthOnBackWall(project, source)
            );
            const sourceMezzanine =
              mezzanineFor(project, source.id) ??
              currentBases.map((base) => mezzanineFor(project, base.id)).find(Boolean);
            const currentIds = new Set(currentBases.flatMap(base => {
              const upper = mezzanineFor(project, base.id);
              return upper ? [base.id, upper.id] : [base.id];
            }));
            const firstIndex = Math.min(
              ...project.modules
                .map((module, index) => currentIds.has(module.id) ? index : Infinity)
            );
            const startOffset = currentBases.reduce(
              (minimum, base) => Math.min(minimum, base.wallOffset ?? Infinity),
              Infinity
            );
            const leftFiller = currentBases[0]?.fillerLeft ?? source.fillerLeft;
            const rightFiller = currentBases.at(-1)?.fillerRight ?? source.fillerRight;
            const primaryId = primary.id;
            Object.assign(primary, structuredClone(source), {
              id: primaryId,
              cabinetRunId: undefined,
              type: width <= 600 ? "single" : "double",
              width,
              wallOffset: Number.isFinite(startOffset) ? startOffset : source.wallOffset,
              fillerLeft: leftFiller,
              fillerRight: rightFiller
            });
            delete primary.cabinetRunId;
            const rebuilt: WardrobeModule[] = [primary];

            if (sourceMezzanine) {
              const upper: WardrobeModule = {
                ...structuredClone(sourceMezzanine),
                cabinetRunId: undefined,
                stackedOnId: primary.id,
                wallOffset: primary.wallOffset,
                width,
                depth: primary.depth,
                fillerLeft: primary.fillerLeft,
                fillerRight: primary.fillerRight
              };
              delete upper.cabinetRunId;
              rebuilt.push(upper);
            }

            project.modules = project.modules.filter(
              module => !currentIds.has(module.id)
            );
            project.modules.splice(firstIndex, 0, ...rebuilt);
            project.modules.forEach(module => {
              if (!module.endAttachedToId || !currentIds.has(module.endAttachedToId)) return;
              module.endAttachedToId = primary.id;
            });
            // Шире — значит, возможно, на прежнем месте уже не помещается (упирается
            // в угол или соседа): ставим на ближайшее свободное место вместе с торцами.
            const currentStart = materializeWallOffsets(project).get(primary.id) ?? 0;
            const placed = placeModuleGroup(project, primary.id, currentStart, 0);
            if (placed !== null && Math.abs(placed - currentStart) > 0.5) {
              primary.wallOffset = placed;
              rebuilt.slice(1).forEach(upper => { upper.wallOffset = placed; });
            }
            project.selectedModuleId = primary.id;
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;
          }),

        setShelvingTotalHeight: (id, requestedHeight) =>
          commit((project) => {
            const selected = project.modules.find(module => module.id === id);
            const base = baseModuleFor(project, selected);
            if (!base || base.type !== "shelving" || base.stackedOnId) return false;
            const current = shelvingUpperFor(project, base.id);
            const availableHeight = Math.min(
              SHELVING_MAX_HEIGHT_MM + SHELVING_MAX_MEZZANINE_HEIGHT_MM,
              usableRoomHeightMm(project) - topFillerMm(base)
            );
            // Всегда одинаково (11.10): стеллаж до 2750, остаток — антресоль 300–600.
            // Раньше при уже стоящей антресоли менялась только она (2600 + 600 вместо 2750 + 450).
            const split = shelvingHeightSplit(
              Math.min(Math.round(requestedHeight), availableHeight)
            );
            base.height = split.baseHeight;
            clearShelvingDesignLink(base);
            if (!split.mezzanineHeight) {
              if (current) {
                project.modules = project.modules.filter(
                  module => module.id !== current.id
                );
              }
            } else if (current) {
              current.height = split.mezzanineHeight;
            } else {
              const upper = createStackedShelving(base, split.mezzanineHeight);
              const baseIndex = project.modules.findIndex(
                module => module.id === base.id
              );
              project.modules.splice(baseIndex + 1, 0, upper);
            }
            project.selectedModuleId = base.id;
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;
          }),

        setShelvingMezzanineHeight: (id, requestedHeight) =>
          commit((project) => {
            const selected = project.modules.find(module => module.id === id);
            const base = baseModuleFor(project, selected);
            if (!base || base.type !== "shelving" || base.stackedOnId) return false;
            const upper = shelvingUpperFor(project, base.id);
            if (!upper) return false;
            const total = base.height + upper.height;
            const range = shelvingMezzanineHeightRange(total);
            const height = Math.min(range.max, Math.max(range.min, Math.round(requestedHeight)));
            if (height === upper.height) return false;
            upper.height = height;
            base.height = total - height;
            clearShelvingDesignLink(base);
            clearShelvingDesignLink(upper);
          }),

        addItem: (type) =>
          commit((project) => {
            /**
             * Новая полка, штанга или обувница встаёт в середину самого большого
             * свободного промежутка, а не вплотную к соседней (приёмка 04.10, wf-01).
             */
            const largestGapMiddle = (target: WardrobeModule) => {
              const spans = target.items
                .filter(existing => existing.type !== "divider")
                .map(existing => internalItemSpanMm(target, existing))
                .sort((first, second) => first[0] - second[0]);
              // Дно внутри — над цоколем и наружными ящиками (проверка 07.10).
              let low = dividerSpanLimits(target).bottom;
              const top = target.height - CARCASS_MM;
              let best: [number, number] = [low, top];
              let bestSize = -1;
              for (const [from, to] of spans) {
                if (from - low > bestSize) { best = [low, from]; bestSize = from - low; }
                low = Math.max(low, to);
              }
              if (top - low > bestSize) best = [low, top];
              return (best[0] + best[1]) / 2;
            };
            /** Свободный промежуток по высоте вокруг середины самого большого — для перегородки. */
            const largestGap = (target: WardrobeModule): [number, number] => {
              const middle = largestGapMiddle(target);
              const spans = target.items
                .filter(existing => existing.type !== "divider")
                .map(existing => internalItemSpanMm(target, existing));
              const below = Math.max(dividerSpanLimits(target).bottom, ...spans.filter(span => span[1] <= middle).map(span => span[1]));
              const above = Math.min(target.height - CARCASS_MM, ...spans.filter(span => span[0] >= middle).map(span => span[0]));
              return [below, above];
            };
            /**
             * Перегородка по умолчанию — под нижнюю полку: от дна (или верха
             * ящиков, корзин, обувницы внизу) до первой полки над ними
             * (приёмка 05.10, wf-07). Если под нижней полкой штанга или
             * пантограф, или полок нет — самый большой свободный промежуток.
             */
            const dividerSpan = (target: WardrobeModule, x: number): [number, number] => {
              const bottomStack = new Set<InternalItem["type"]>(["drawer", "basket", "leatherBasket", "shoeRack", "trouserRack"]);
              const spans = target.items
                .filter(existing => existing.type !== "divider" && existing.type !== "led")
                .filter(existing => internalItemPieces(target, existing)
                  .some(piece => x >= piece.left - 0.5 && x <= piece.right + 0.5))
                .map(existing => ({ type: existing.type, span: internalItemSpanMm(target, existing) }))
                .sort((first, second) => first.span[0] - second.span[0]);
              const lowestShelf = spans.find(entry => entry.type === "shelf");
              if (!lowestShelf) return largestGap(target);
              const below = spans.filter(entry => entry !== lowestShelf && entry.span[1] <= lowestShelf.span[0] + 0.5);
              if (below.some(entry => !bottomStack.has(entry.type))) return largestGap(target);
              const from = Math.max(dividerSpanLimits(target).bottom, ...below.map(entry => entry.span[1]));
              const to = lowestShelf.span[0];
              return to - from >= 328 ? [from, to] : largestGap(target);
            };
            const module = project.modules.find(
              (item) => item.id === project.selectedModuleId
            );
            if (!module) return;
            if (module.type === "shelving" || module.type === "end") return;
            if (module.type === "corner45" && type !== "shelf" && type !== "rail") return false;
            const definition = INTERNALS[type];
            const spreadsOut = (type === "shelf" || type === "rail" || type === "shoeRack") &&
              module.type !== "corner45" && module.items.some(existing => existing.type !== "divider");
            const preferredY = spreadsOut
              ? largestGapMiddle(module)
              : type === "shelf" && module.fillingPreset
                ? module.height * 0.72
                : definition.defaultY;
            const item: InternalItem = {
              id: createId("item"),
              type,
              y: clamp(snap(preferredY), 80, module.height - 80)
            };
            if (definition.height) item.height = definition.height;
            // Подсветка «в полках»: новая полка тоже со своей лентой.
            if (type === "shelf" && cabinetLightMode(module) === "shelves") item.light = true;
            if (type === "divider") {
              item.x = defaultDividerX(module);
              // Перегородка — под нижнюю полку, на всю высоту свободного промежутка.
              // Раньше 1400 мм посередине — короткая и резала пантограф (приёмка 04.10, wf-07).
              // Зазор 4 мм сверху и снизу: после привязки к шагу перегородка
              // не должна задевать полку или ящики.
              const [from, to] = dividerSpan(module, item.x);
              item.height = Math.max(320, Math.round(to - from) - 8);
              item.y = Math.round((from + to) / 2);
            }
            if (module.type === "corner45") {
              // Штанги — не больше двух, обе в корпусе А (приёмка 08.10).
              if (type === "rail" && !cornerCanAddRail(module)) return false;
              const position = type === "rail"
                ? cornerRailPlacement(module, item, item.y)
                : cornerItemPosition(module, item, item.y);
              if (position === undefined) return false;
              item.y = position;
            }
            module.items.push(item);
            if (module.type !== "corner45") item.y = resolveInternalItemY(module, item.id, item.y);
            module.fillingPreset = undefined;
            project.selectedItemId = item.id;
            project.selectedShelvingCellId = null;
          }),

        updateItem: (moduleId, itemId, patch) =>
          commit((project) => {
            const module = project.modules.find((item) => item.id === moduleId);
            const item = module?.items.find((candidate) => candidate.id === itemId);
            if (!module || !item) return;
            if (module.type === "corner45") {
              const candidate = { ...item, ...patch };
              if (candidate.type !== "shelf" && candidate.type !== "rail") return false;
              const y = cornerItemPosition(module, candidate, candidate.y);
              if (y === undefined) return false;
              Object.assign(item, candidate, { y });
              delete item.cornerRailWall;
              return;
            }
            // Высота внутреннего ящика (31.10): низ на месте, ящик растёт вверх.
            if (item.type === "drawer" && patch.height !== undefined) {
              const resized = resizeInternalDrawer(module, itemId, patch.height);
              if (!resized) return false;
              item.height = resized.height;
              item.y = resized.y;
              module.fillingPreset = undefined;
              return;
            }
            const currentY = item.y;
            const currentDividerHeight =
              item.height ?? Math.min(1400, module.height - 180);
            const resizedDivider =
              item.type === "divider" && patch.height !== undefined
                ? resizeDividerFromBottom(
                    module.height,
                    item.y,
                    currentDividerHeight,
                    snapDividerHeightToShelves(
                      module,
                      item.id,
                      item.y,
                      currentDividerHeight,
                      patch.height
                    )
                  )
                : null;
            Object.assign(item, patch);
            if (item.type === "divider" && patch.x !== undefined) {
              item.x = clampDividerX(module, patch.x);
            }
            if (item.type === "divider" && patch.height !== undefined) {
              if (resizedDivider) {
                item.height = resizedDivider.height;
              }
              if (patch.y === undefined && resizedDivider) {
                item.y = resizedDivider.y;
              }
            }
            if (item.type === "divider" && (patch.y !== undefined || patch.x !== undefined)) {
              // Перегородку тянули: она встаёт в пролёт от опоры до опоры (22.10).
              const before = { ...item, y: currentY, height: currentDividerHeight };
              const move = resolveDividerMove(module, before, patch.y ?? currentY, item.x ?? 0);
              if (move) {
                item.y = move.y;
                item.height = move.height;
              }
            } else if (patch.y !== undefined) {
              item.y = resolveInternalItemY(
                module,
                itemId,
                patch.y,
                Math.sign(patch.y - currentY)
              );
            }
            module.fillingPreset = undefined;
          }),

        setDividerEdge: (moduleId, itemId, edge, value) =>
          commit((project) => {
            const module = project.modules.find((candidate) => candidate.id === moduleId);
            const item = module?.items.find((candidate) => candidate.id === itemId);
            if (!module || !item || item.type !== "divider") return false;
            const span = resolveDividerEdge(module, item, edge, value);
            item.height = span.height;
            item.y = span.y;
            module.fillingPreset = undefined;
          }),

        // Полка через несколько отсеков — одна строка; свет под ней в одном отсеке
        // выделяет этот отсек в свою полку (перепроверка 07.10, wf-15).
        setShelfPieceLight: (moduleId, itemId, pieceCenterX, on) =>
          commit((project) => {
            const module = project.modules.find((candidate) => candidate.id === moduleId);
            const item = module?.items.find((candidate) => candidate.id === itemId);
            if (!module || !item || item.type !== "shelf" || module.type === "corner45") return false;
            const parts = splitItemByCompartments(module, item, pieceCenterX, () => createId("item"));
            if (parts.length > 1) module.items.splice(module.items.indexOf(item), 1, ...parts);
            const target = module.items.find((candidate) => candidate.id === itemId)!;
            if (on) target.light = true;
            else delete target.light;
            project.selectedModuleId = moduleId;
            project.selectedItemId = itemId;
            module.fillingPreset = undefined;
          }),

        moveItemInCompartment: (moduleId, itemId, x, y) =>
          commit((project) => {
            const module = project.modules.find((candidate) => candidate.id === moduleId);
            const item = module?.items.find((candidate) => candidate.id === itemId);
            if (!module || !item || item.type === "divider" || module.type === "corner45") return false;
            const currentY = item.y;
            const parts = splitItemByCompartments(module, item, x, () => createId("item"));
            if (parts.length > 1) module.items.splice(module.items.indexOf(item), 1, ...parts);
            const target = module.items.find((candidate) => candidate.id === itemId)!;
            target.y = resolveInternalItemY(module, itemId, y, Math.sign(y - currentY));
            project.selectedModuleId = moduleId;
            project.selectedItemId = itemId;
            module.fillingPreset = undefined;
          }),

        applyFillingPreset: (preset) =>
          commit((project) => {
            const module = project.modules.find(
              (candidate) => candidate.id === project.selectedModuleId
            );
            if (!module || !fillingPresetFits(module, preset)) return;
            module.items = buildFillingPreset(
              module,
              preset,
              () => createId("item")
            );
            module.fillingPreset = preset;
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;
          }),

        copyFillingToModule: (targetModuleId) =>
          commit((project) => {
            const source = project.modules.find(
              (candidate) => candidate.id === project.selectedModuleId
            );
            const target = project.modules.find(
              (candidate) => candidate.id === targetModuleId
            );
            if (
              !source ||
              !target ||
              source.id === target.id ||
              source.type === "shelving" ||
              source.type === "corner45" ||
              source.type === "mezzanine" ||
              source.type === "end" ||
              target.type === "shelving" ||
              target.type === "corner45" ||
              target.type === "mezzanine"
              || target.type === "end"
            ) {
              return;
            }
            target.items = copyFillingLayout(
              source,
              target,
              () => createId("item")
            );
            target.fillingPreset = source.fillingPreset;
            target.decorDensity = source.decorDensity ?? "normal";
            project.selectedItemId = null;
            project.selectedShelvingCellId = null;
          }),

        setDecorDensity: (density) =>
          commit((project) => {
            const module = project.modules.find(
              (candidate) => candidate.id === project.selectedModuleId
            );
            if (!module || module.type === "shelving" || module.type === "end") return;
            module.decorDensity = density;
          }),

        setDiagonalSection: (moduleId, area, sourceId, replaceExisting = false) => {
          let applied = false;
          commit((project) => {
            const index = project.modules.findIndex(module => module.id === moduleId && module.type === "shelving");
            if (index < 0) return false;
            const updated = applyDiagonalArea(project.modules[index]!, area, sourceId, replaceExisting);
            if (!updated) return false;
            clearShelvingDesignLink(updated);
            project.modules[index] = updated;
            project.selectedModuleId = moduleId;
            project.selectedItemId = null;
            project.selectedShelvingCellId = shelvingCellId(moduleId, area.left, area.bottom);
            applied = true;
          });
          return applied;
        },

        mergeShelvingCells: (moduleId, area, keepCellId) => {
          let problem: string | null = null;
          commit((project) => {
            const module = project.modules.find(candidate => candidate.id === moduleId && candidate.type === "shelving");
            if (!module) { problem = "Стеллаж не найден."; return false; }
            problem = shelvingMergeProblem(module, area);
            if (problem) return false;
            const anchorId = shelvingCellId(moduleId, area.left, area.bottom);
            const inside = (cell: ShelvingCellConfig) => cell.column >= area.left && cell.column <= area.right &&
              cell.row >= area.bottom && cell.row <= area.top;
            const anchor = shelvingMergeKeptCell(module.shelvingCells ?? [], area, keepCellId, anchorId)
              ?? defaultShelvingCell(moduleId, area.left, area.bottom);
            module.shelvingCells = [
              ...(module.shelvingCells ?? []).filter(cell => !inside(cell)),
              { ...anchor, id: anchorId, column: area.left, row: area.bottom,
                mergeColumnSpan: area.right - area.left + 1, mergeRowSpan: area.top - area.bottom + 1,
                leftDividerAngle: 0 }
            ];
            clearShelvingDesignLink(module);
            project.selectedModuleId = moduleId;
            project.selectedItemId = null;
            project.selectedShelvingCellId = anchorId;
          });
          return problem;
        },

        setShelvingUprightAngle: (moduleId, dividerIndex, angle, onlyRow) => {
          let turned: number[] = [];
          commit((project) => {
            const module = project.modules.find(candidate => candidate.id === moduleId && candidate.type === "shelving");
            if (!module) return false;
            const grid = getShelvingGridLayout(module);
            if (dividerIndex < 0 || dividerIndex >= grid.columns - 1) return false;
            const value = standardShelvingAngle(angle);
            const rotatable = shelvingUprightRotatableRows(module, dividerIndex, grid);
            const rows = onlyRow === undefined
              ? value === 0 ? Array.from({ length: grid.rows }, (_, row) => row) : rotatable
              : rotatable.includes(onlyRow) || value === 0 ? [onlyRow] : [];
            if (!rows.length) return false;
            // Предел поворота — по ширинам ячеек именно этих рядов (№ 19).
            const maximum = Math.min(...rows.map((row) => shelvingMainDividerMaxAngle(
              gridCellWidth(grid, row, dividerIndex), gridCellWidth(grid, row, dividerIndex + 1), module.depth)));
            if (value !== 0 && maximum < SHELVING_STANDARD_ANGLE_DEG) return false;
            module.shelvingCells ??= [];
            for (const row of rows) {
              const id = shelvingCellId(moduleId, dividerIndex + 1, row);
              let cell = module.shelvingCells.find(candidate => candidate.id === id);
              if (!cell) {
                if (value === 0) continue;
                cell = defaultShelvingCell(moduleId, dividerIndex + 1, row);
                module.shelvingCells.push(cell);
              }
              cell.leftDividerAngle = value;
            }
            turned = value === 0 ? [] : rows;
            clearShelvingDesignLink(module);
          });
          return turned;
        },

        setShelvingCellWidth: (moduleId, row, column, width, scope) => {
          let changed = false;
          commit((project) => {
            const module = project.modules.find(candidate => candidate.id === moduleId && candidate.type === "shelving");
            if (!module) return false;
            const grid = getShelvingGridLayout(module);
            if (row < 0 || row >= grid.rows || column < 0 || column >= grid.columns || grid.columns < 2) return false;
            if (scope === "row" && (!shelvingKindAllowsRowColumns(module) ||
              rowsInTallSpans(module.shelvingCells ?? []).has(row))) return false;
            const source = scope === "row" ? grid.rowCellWidths[row] ?? grid.cellWidths : grid.cellWidths;
            const widths = moveOneShelvingUpright(source, column, width, SHELVING_MIN_CELL_WIDTH_MM);
            if (!widths) return false;
            const total = widths.reduce((sum, value) => sum + value, 0);
            const ratios = widths.map((value) => value / total);
            if (scope === "all") {
              module.shelvingColumnRatios = ratios;
            } else {
              const own = Array.from({ length: grid.rows }, (_, index) => module.shelvingRowColumnRatios?.[index] ?? null);
              own[row] = ratios;
              module.shelvingRowColumnRatios = own;
            }
            clearShelvingDesignLink(module);
            changed = true;
          });
          return changed;
        },

        resetShelvingRowColumns: (moduleId, row) => commit((project) => {
          const module = project.modules.find(candidate => candidate.id === moduleId && candidate.type === "shelving");
          if (!module?.shelvingRowColumnRatios?.[row]) return false;
          module.shelvingRowColumnRatios = module.shelvingRowColumnRatios.map((value, index) => index === row ? null : value);
          clearShelvingDesignLink(module);
        }),

        splitShelvingCell: (moduleId, cellId) => commit((project) => {
          const module = project.modules.find(candidate => candidate.id === moduleId && candidate.type === "shelving");
          const cell = module?.shelvingCells?.find(candidate => candidate.id === cellId);
          if (!module || !cell || ((cell.mergeColumnSpan ?? 1) < 2 && (cell.mergeRowSpan ?? 1) < 2)) return false;
          cell.mergeColumnSpan = undefined;
          cell.mergeRowSpan = undefined;
          clearShelvingDesignLink(module);
        }),

        removeDiagonalSection: (moduleId, cellId) => commit((project) => {
          const module = project.modules.find(candidate => candidate.id === moduleId && candidate.type === "shelving");
          if (!module?.shelvingCells?.some(cell => cell.id === cellId && cell.content === "diagonal")) return false;
          module.shelvingCells = module.shelvingCells.filter(cell => cell.id !== cellId);
          clearShelvingDesignLink(module);
          project.selectedModuleId = moduleId;
          project.selectedItemId = null;
          project.selectedShelvingCellId = cellId;
        }),

        applyShelvingCellFilling: (moduleId, sourceCellId, targetCellIds) => {
          let applied = 0;
          commit((project) => {
            const module = project.modules.find(
              (candidate) => candidate.id === moduleId
            );
            if (!module || module.type !== "shelving") return false;
            const plan = shelvingBulkPlan(module, sourceCellId, targetCellIds);
            if (!plan.apply.length) return false;
            module.shelvingCells = applyShelvingBulkPlan(module, plan);
            clearShelvingDesignLink(module);
            project.selectedModuleId = moduleId;
            project.selectedItemId = null;
            applied = plan.apply.length;
          });
          return applied;
        },

        updateAllShelvingCells: (moduleId, patch) =>
          commit((project) => {
            const module = project.modules.find((candidate) => candidate.id === moduleId);
            if (!module || module.type !== "shelving" || !module.shelvingCells?.length) return false;
            // Меняются только отделка и фурнитура: содержимое и геометрия ячеек не трогаются.
            const { id: _id, column: _column, row: _row, content: _content, mergeColumnSpan: _mc, mergeRowSpan: _mr,
              diagonalColumnSpan: _dc, diagonalRowSpan: _dr, ...finish } = patch;
            // Стеклянная дверца комода общую отделку не принимает — у неё своя (15.10).
            const chest = module.shelvingKind === "chest";
            module.shelvingCells.forEach((cell) => { if (!(chest && isChestGlassDoor(cell))) Object.assign(cell, finish); });
            clearShelvingDesignLink(module);
          }),
        updateShelvingCell: (moduleId, cellId, patch) =>
          commit((project) => {
            // Region edits go through the explicit, conflict-checked transaction.
            if (patch.content === "diagonal") return false;
            const module = project.modules.find(
              (candidate) => candidate.id === moduleId
            );
            const parsed = parseShelvingCellId(cellId);
            if (
              !module ||
              module.type !== "shelving" ||
              !parsed ||
              parsed.moduleId !== moduleId
            ) {
              return;
            }
            module.shelvingCells ??= [];
            let cell = module.shelvingCells.find(
              (candidate) => candidate.id === cellId
            );
            if (cell?.content === "diagonal" && (patch.content ||
              patch.diagonalColumnSpan !== undefined || patch.diagonalRowSpan !== undefined)) return false;
            if (!cell) {
              cell = defaultShelvingCell(
                moduleId,
                parsed.column,
                parsed.row
              );
              module.shelvingCells.push(cell);
            }
            Object.assign(cell, patch, {
              id: cellId,
              column: parsed.column,
              row: parsed.row
            });
            clearShelvingDesignLink(module);
            if (cell.content === "diagonal") {
              cell.extraShelf = false;
              const rect = getShelvingCellSpanRect(
                module,
                parsed.column,
                parsed.row,
                cell.diagonalColumnSpan ?? 2,
                cell.diagonalRowSpan ?? 2
              );
              cell.diagonalColumnSpan = rect.columnSpan;
              cell.diagonalRowSpan = rect.rowSpan;
              module.shelvingCells = normalizeShelvingDiagonalCells(
                module.id,
                module.shelvingCells,
                module.shelvingColumns ?? 4,
                module.shelvingRows ?? 5
              );
            }
            const selectedDiagonal = getShelvingDiagonalSections(module).find(
              (section) => section.coveredCellIds.includes(cellId)
            );
            project.selectedModuleId = moduleId;
            project.selectedItemId = null;
            project.selectedShelvingCellId = selectedDiagonal?.cell.id ?? cellId;
          }),

        removeSelectedItem: () =>
          commit((project) => {
            const module = project.modules.find(
              (item) => item.id === project.selectedModuleId
            );
            if (!module || !project.selectedItemId) return;
            module.items = module.items.filter(
              (item) => item.id !== project.selectedItemId
            );
            module.fillingPreset = undefined;
            project.selectedItemId = null;
          }),

        setMaterial: (material) =>
          commit((project) => {
            const module = project.modules.find(
              (item) => item.id === project.selectedModuleId
            );
            if (module) {
              module.material = material;
              if (module.type !== "end" && module.type !== "mezzanine") {
                syncAttachedEndModuleMaterials(project, module.id, material);
              }
            } else {
              project.carcassMaterial = material;
            }
          }),

        setDecor: (code) =>
          commit((project) => {
            const decor = resolveDecor(code);
            const material = decorPriceMaterial(decor.code);
            const module = project.modules.find(
              (item) => item.id === project.selectedModuleId
            );
            if (module) {
              // Однотонная перекраска и эмаль корпуса снимаются: декор ЛДСП
              // рисуется поверх них и иначе был бы не виден. Антресоль и торцы —
              // в том же декоре; свой декор антресоли задают, выбрав её саму.
              const partner = cornerPartner(project, module);
              applyCarcassDecor(project, module, decor.code, material);
              // Угловой и сосед — в одном декоре (29.10).
              if (partner) applyCarcassDecor(project, partner, decor.code, material);
            } else {
              project.carcassDecor = decor.code;
              project.carcassMaterial = material;
            }
          }),

        loadProject: (project) => set({ project: normalizeProject(clone(project)), past: [], future: [] }),
        setProjectName: (name) =>
          commit((project) => {
            project.name = name.trim().slice(0, 60) || "Новый проект";
          }),

        setRoomStyle: (style) =>
          commit((project) => {
            Object.assign(project.room, roomStylePatch(style));
          }),

        setRoom: (patch) =>
          commit((project) => {
            Object.assign(project.room, patch);
            if (
              patch.style === undefined &&
              (patch.wallColor !== undefined ||
                patch.floor !== undefined ||
                patch.lighting !== undefined)
            ) {
              project.room.style = "custom";
            }
          }),

        setRoomWidth: (width) =>
          commit((project) => {
            // Шкафы у задней стены стоят на месте (отсчёт от левого угла),
            // шкафы у правой стены и правый угловой едут вместе со стеной.
            const previous = roomWidthMm(project);
            const next = Math.min(MAX_ROOM_WIDTH_MM, Math.max(roomWidthMinimumMm(project), Math.round(width)));
            if (next === previous) return false;
            const resized = withRoomWidth(project, next);
            project.modules = resized.modules;
            project.room = resized.room;
          }),

        undo: () =>
          set((state) => {
            const previous = state.past.at(-1);
            if (!previous) return state;
            return {
              project: normalizeProject(clone(previous)),
              past: state.past.slice(0, -1),
              future: [clone(state.project), ...state.future].slice(0, 30)
            };
          }),

        redo: () =>
          set((state) => {
            const next = state.future[0];
            if (!next) return state;
            return {
              project: normalizeProject(clone(next)),
              past: [...state.past, clone(state.project)].slice(-30),
              future: state.future.slice(1)
            };
          }),

        reset: (options) =>
          set((state) => ({
            // Новый проект — новый номер: normalizeProject выдаёт его сам.
            // Комната остаётся, если просили (смена раздела, «Начать новый проект»):
            // раньше при переходе из стеллажей в шкафы сбрасывались потолок и стены (приёмка 04.10, wb-01).
            project: normalizeProject({
              ...clone(initialProject),
              ...(options?.keepRoom ? { room: structuredClone(state.project.room) } : {})
            }),
            past: [...state.past, clone(state.project)].slice(-30),
            future: []
          }))
      };
    },
    {
      name: plannerStorageKey(),
      version: PLANNER_STORE_VERSION,
      migrate: (persistedState) => {
        const state = persistedState as Partial<PlannerState>;
        return {
          ...state,
          project: state.project
            ? normalizeProject(state.project)
            : normalizeProject(clone(initialProject))
        } as PlannerState;
      },
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<PlannerState>;
        return {
          ...currentState,
          ...persisted,
          project: persisted.project
            ? normalizeProject(clone(persisted.project))
            : currentState.project,
          past: [],
          future: []
        };
      },
      partialize: (state) => ({ project: state.project })
    }
  )
);
